/**
 * opsec.tsx — Full OPSEC monitoring dashboard.
 *
 * Mirrors the existing admin-opsec.ejs in the user's other SaaS app
 * (nostrmag, same VPS) — same data, same actions, same log semantics.
 * Reachable from NetworkView's OPSEC tab and from the HamburgerMenu.
 *
 * Sections (matching the EJS):
 *   1. System stats (connections / drop / load / uptime)
 *   2. HTTP status codes (last hour) + Top IPs + Top Paths
 *   3. Suspicious activity (filterable table with per-row actions)
 *   4. UFW Firewall (block form + blocked list + block log)
 *   5. Nginx Deny List (add form + current list + deny log)
 *   6. Whitelisted IPs (list with remove)
 *
 * Plus the AbuseIPDB report modal (helper to compose a comment from
 * the reasons seen in the suspicious row).
 *
 * All backend calls go through services/api.ts (opsec.*). Role-gating
 * is enforced by the App.tsx route (superadmin / sadmin only) and
 * re-checked by the backend's requireOpsAdmin middleware.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, RefreshCw, Shield, Ban, CheckCircle2,
  Activity, Network, ListOrdered, X,
  ExternalLink, Eye, Copy, AlertTriangle, Server, FileText,
  ChevronDown, ChevronUp, Sun, Moon,
  Globe, Clock, Zap, Menu,
} from 'lucide-react';
// d3-geo, topojson-client, d3-selection, d3-zoom all ship without bundled
// .d.ts files and we deliberately don't pull in @types/d3-* (saves ~5 MB of
// declarations we don't use). We tried an ambient `declarations.d.ts` next
// to this file but VSCode's TS Language Server doesn't always pick it up
// (depends on the project's tsconfig include paths). The most reliable
// way to silence the "no declaration file" error is a per-line @ts-ignore
// — the imports still work at runtime, this just stops the type checker
// from complaining about the missing .d.ts file.
// @ts-ignore  -- d3-geo has no bundled types
import { geoEqualEarth, geoPath, geoCentroid, geoBounds } from 'd3-geo';
// @ts-ignore  -- topojson-client has no bundled types
import { feature } from 'topojson-client';
// @ts-ignore  -- d3-selection has no bundled types
import { select } from 'd3-selection';
// @ts-ignore  -- d3-zoom has no bundled types
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const IPV4_RE = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const CIDR_RE = /^(?:\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;

function isValidIp(s: string) { return IPV4_RE.test(s); }
function isValidCidr(s: string) { return IPV4_RE.test(s.split('/')[0]) && CIDR_RE.test(s); }

// Best-effort path guess for the report modal when the only signal we have
// is the reason text from the deny log (the deny log doesn't store the
// original request path — just IP + reason). The report uses this as a
// hint, not a definitive claim.
function guessReconPathFromReason(reason: string): string {
  const r = (reason || '').toLowerCase();
  if (/wp-admin/.test(r)) return '/wp-admin/';
  if (/wp-login/.test(r)) return '/wp-login.php';
  if (/wp-content/.test(r)) return '/wp-content/...';
  if (/xml-rpc|xmlrpc/.test(r)) return '/xmlrpc.php';
  if (/\.env/.test(r)) return '/.env';
  if (/\.git/.test(r)) return '/.git/HEAD';
  if (/config\.json/.test(r)) return '/config.json';
  if (/php/.test(r)) return '/path/file.php';
  return '/(unknown)';
}

// Country code → flag emoji. Two regional indicator letters per country.
// Returns a flag emoji like 🇺🇸 for "US", or "" for unknown/empty.
function countryFlag(code: string): string {
  if (!code || code.length !== 2) return '';
  const A = 0x1F1E6;
  const upper = code.toUpperCase();
  return String.fromCodePoint(A + (upper.charCodeAt(0) - 65), A + (upper.charCodeAt(1) - 65));
}

// Country lat/lng centroids — used to position bubbles on the world map.
// Covers the top ~60 most common attacker countries. ip-api.com doesn't
// return lat/lng, so we hardcode the centroid. Close enough for a bubble map.
const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  US: [39.83, -98.58], CA: [56.13, -106.35], MX: [23.63, -102.55],
  BR: [-14.24, -51.93], AR: [-38.42, -63.62], CL: [-35.68, -71.54],
  CO: [4.57, -74.30], PE: [-9.19, -75.02], VE: [6.42, -66.59],
  GB: [55.38, -3.44], IE: [53.41, -8.24], FR: [46.23, 2.21],
  DE: [51.17, 10.45], NL: [52.13, 5.29], BE: [50.50, 4.47],
  ES: [40.46, -3.75], PT: [39.40, -8.22], IT: [41.87, 12.57],
  CH: [46.82, 8.23], AT: [47.52, 14.55], PL: [51.92, 19.15],
  CZ: [49.82, 15.47], SK: [48.67, 19.70], HU: [47.16, 19.50],
  RO: [45.94, 24.97], BG: [42.73, 25.49], GR: [39.07, 21.82],
  TR: [38.96, 35.24], UA: [48.38, 31.17], RU: [61.52, 105.32],
  BY: [53.71, 27.95], SE: [60.13, 18.64], NO: [60.47, 8.47],
  FI: [61.92, 25.75], DK: [56.26, 9.50], IS: [64.96, -19.02],
  CN: [35.86, 104.20], JP: [36.20, 138.25], KR: [35.91, 127.77],
  KP: [40.34, 127.51], TW: [23.70, 120.96], HK: [22.32, 114.17],
  IN: [20.59, 78.96], PK: [30.38, 69.35], BD: [23.68, 90.36],
  VN: [14.06, 108.28], TH: [15.87, 100.99], ID: [-0.79, 113.92],
  PH: [12.88, 121.77], MY: [4.21, 101.98], SG: [1.35, 103.82],
  AU: [-25.27, 133.78], NZ: [-40.90, 174.89], FJ: [-16.58, 179.41],
  ZA: [-30.56, 22.94], EG: [26.82, 30.80], NG: [9.08, 8.68],
  KE: [-0.02, 37.91], MA: [31.79, -7.09], DZ: [28.03, 1.66],
  TN: [33.89, 9.54], LY: [26.34, 17.23], SD: [12.86, 30.22],
  ET: [9.15, 40.49], GH: [7.95, -1.02], CI: [7.54, -5.55],
  IL: [31.05, 34.85], SA: [23.89, 45.08], AE: [23.42, 53.85],
  IR: [32.43, 53.69], IQ: [33.22, 43.68], JO: [30.59, 36.24],
  LB: [33.85, 35.86], SY: [34.80, 38.99], YE: [15.55, 48.52],
  KZ: [48.02, 66.92], UZ: [41.38, 64.59], TM: [38.97, 59.56],
  GE: [42.32, 43.36], AM: [40.07, 45.04], AZ: [40.14, 47.58],
};

// Equirectangular projection. Maps (lat, lng) to SVG (x, y).
//  width=1000, height=500 → standard world map aspect ratio.
function projectLatLng(lat: number, lng: number, width: number, height: number): { x: number; y: number } {
  const x = (lng + 180) * (width / 360);
  const y = (90 - lat) * (height / 180);
  return { x, y };
}

// Theme hook — watches the `dark` class on <html>. Used by the world map
// to pick light/dark colors without needing to re-render on every change.
function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

// Color interpolation for attacker countries. Theme-aware so the red scale
// looks good on both light and dark backgrounds:
//   - Light mode: low count = light pink, high count = deep red
//   - Dark mode:  low count = medium red (visible against dark bg),
//                 high count = bright red
// Returns an HSL color string suitable for SVG fill="..." or stroke="...".
function redScale(t: number, isDark: boolean = false): string {
  const tt = Math.max(0, Math.min(1, t));
  if (isDark) {
    // Dark mode: low = medium red (45% lightness, visible on dark bg),
    //            high = bright red (70% lightness)
    const lightness = 45 + tt * 25;   // 45% → 70%
    const saturation = 60 + tt * 30;   // 60% → 90%
    return `hsl(0, ${saturation}%, ${lightness}%)`;
  } else {
    // Light mode: low = light pink, high = deep red
    const lightness = 88 - tt * 48;   // 88% → 40%
    const saturation = 70 + tt * 20;   // 70% → 90%
    return `hsl(0, ${saturation}%, ${lightness}%)`;
  }
}

// Lighter red scale for the country FILL. Always in a lighter range than
// redScale so the bubble (which uses redScale) stands out against the country.
//   Light mode: 92% → 75% lightness (light pink to medium pink)
//   Dark mode:  20% → 32% lightness (very dark red to medium dark red)
function countryRedScale(t: number, isDark: boolean = false): string {
  const tt = Math.max(0, Math.min(1, t));
  if (isDark) {
    const lightness = 20 + tt * 12;   // 20% → 32%
    const saturation = 50 + tt * 30;   // 50% → 80%
    return `hsl(0, ${saturation}%, ${lightness}%)`;
  } else {
    const lightness = 92 - tt * 17;   // 92% → 75%
    const saturation = 60 + tt * 15;   // 60% → 75%
    return `hsl(0, ${saturation}%, ${lightness}%)`;
  }
}

// World map TopoJSON — 110m resolution (small, ~108KB, ships with the
// project's public/ directory). Source: world-atlas@2 on npm (Natural
// Earth data, public domain). Loaded from `/world-110m.json` so it works
// offline once the file is in public/.
//
// The file has 177 country features with `properties.name` (e.g. "United
// States of America"). We map these names to ISO 3166-1 alpha-2 codes
// (e.g. "US") via COUNTRY_NAME_TO_ISO below, so we can match against the
// countryCode field returned by ip-api.com / stored in topCountries.
const WORLD_GEO_URL = '/world-110m.json';

// Mapping from world-atlas country names to ISO 3166-1 alpha-2 codes.
// Covers the ~100 most common countries (every country a real attacker
// could plausibly come from). If a country isn't in this map, the country
// fill is still rendered but no bubble can be placed (we'd have no
// centroid to position the bubble on).
const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  'Afghanistan': 'AF', 'Albania': 'AL', 'Algeria': 'DZ', 'Angola': 'AO',
  'Argentina': 'AR', 'Armenia': 'AM', 'Australia': 'AU', 'Austria': 'AT',
  'Azerbaijan': 'AZ', 'Bahamas': 'BS', 'Bangladesh': 'BD', 'Belarus': 'BY',
  'Belgium': 'BE', 'Belize': 'BZ', 'Benin': 'BJ', 'Bhutan': 'BT',
  'Bolivia': 'BO', 'Bosnia and Herz.': 'BA', 'Botswana': 'BW', 'Brazil': 'BR',
  'Brunei': 'BN', 'Bulgaria': 'BG', 'Burkina Faso': 'BF', 'Burundi': 'BI',
  'Cambodia': 'KH', 'Cameroon': 'CM', 'Canada': 'CA',
  'Central African Rep.': 'CF', 'Chad': 'TD', 'Chile': 'CL', 'China': 'CN',
  'Colombia': 'CO', 'Congo': 'CG', 'Costa Rica': 'CR', 'Croatia': 'HR',
  'Cuba': 'CU', 'Cyprus': 'CY', 'Czech Rep.': 'CZ', 'Czechia': 'CZ',
  "Côte d'Ivoire": 'CI', 'Dem. Rep. Congo': 'CD', 'Denmark': 'DK',
  'Djibouti': 'DJ', 'Dominican Rep.': 'DO', 'Ecuador': 'EC', 'Egypt': 'EG',
  'El Salvador': 'SV', 'Eq. Guinea': 'GQ', 'Eritrea': 'ER', 'Estonia': 'EE',
  'Ethiopia': 'ET', 'Fiji': 'FJ', 'Finland': 'FI', 'France': 'FR',
  'Gabon': 'GA', 'Gambia': 'GM', 'Georgia': 'GE', 'Germany': 'DE',
  'Ghana': 'GH', 'Greece': 'GR', 'Greenland': 'GL', 'Guatemala': 'GT',
  'Guinea': 'GN', 'Guinea-Bissau': 'GW', 'Guyana': 'GY', 'Haiti': 'HT',
  'Honduras': 'HN', 'Hungary': 'HU', 'Iceland': 'IS', 'India': 'IN',
  'Indonesia': 'ID', 'Iran': 'IR', 'Iraq': 'IQ', 'Ireland': 'IE',
  'Israel': 'IL', 'Italy': 'IT', 'Jamaica': 'JM', 'Japan': 'JP',
  'Jordan': 'JO', 'Kazakhstan': 'KZ', 'Kenya': 'KE', 'Korea': 'KR',
  'Kuwait': 'KW', 'Kyrgyzstan': 'KG', 'Laos': 'LA', 'Latvia': 'LV',
  'Lebanon': 'LB', 'Liberia': 'LR', 'Libya': 'LY', 'Lithuania': 'LT',
  'Luxembourg': 'LU', 'Macedonia': 'MK', 'Madagascar': 'MG', 'Malawi': 'MW',
  'Malaysia': 'MY', 'Mali': 'ML', 'Mauritania': 'MR', 'Mexico': 'MX',
  'Moldova': 'MD', 'Mongolia': 'MN', 'Montenegro': 'ME', 'Morocco': 'MA',
  'Mozambique': 'MZ', 'Myanmar': 'MM', 'Namibia': 'NA', 'Nepal': 'NP',
  'Netherlands': 'NL', 'New Caledonia': 'NC', 'New Zealand': 'NZ',
  'Nicaragua': 'NI', 'Niger': 'NE', 'Nigeria': 'NG', 'North Korea': 'KP',
  'Norway': 'NO', 'Oman': 'OM', 'Pakistan': 'PK', 'Panama': 'PA',
  'Papua New Guinea': 'PG', 'Paraguay': 'PY', 'Peru': 'PE',
  'Philippines': 'PH', 'Poland': 'PL', 'Portugal': 'PT', 'Puerto Rico': 'PR',
  'Qatar': 'QA', 'Romania': 'RO', 'Russia': 'RU', 'Rwanda': 'RW',
  'Saudi Arabia': 'SA', 'Senegal': 'SN', 'Serbia': 'RS', 'Sierra Leone': 'SL',
  'Singapore': 'SG', 'Slovakia': 'SK', 'Slovenia': 'SI', 'Solomon Is.': 'SB',
  'Somalia': 'SO', 'South Africa': 'ZA', 'South Korea': 'KR', 'Spain': 'ES',
  'Sri Lanka': 'LK', 'Sudan': 'SD', 'Suriname': 'SR', 'Sweden': 'SE',
  'Switzerland': 'CH', 'Syria': 'SY', 'Taiwan': 'TW', 'Tajikistan': 'TJ',
  'Tanzania': 'TZ', 'Thailand': 'TH', 'Togo': 'TG', 'Trinidad and Tobago': 'TT',
  'Tunisia': 'TN', 'Turkey': 'TR', 'Turkmenistan': 'TM', 'UAE': 'AE',
  'Uganda': 'UG', 'Ukraine': 'UA', 'United Kingdom': 'GB',
  'United States of America': 'US', 'Uruguay': 'UY', 'Uzbekistan': 'UZ',
  'Venezuela': 'VE', 'Vietnam': 'VN', 'W. Sahara': 'EH', 'Yemen': 'YE',
  'Zambia': 'ZM', 'Zimbabwe': 'ZW',
};

// Reverse mapping: ISO → world-atlas name (used to highlight a country).
// Built once from the forward map.
const ISO_TO_COUNTRY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_NAME_TO_ISO).map(([name, iso]) => [iso, name])
);

// World map with attack bubbles. Uses react-simple-maps to render real
// country boundaries from a TopoJSON, plus custom bubbles for each
// attacker country. Inline SVG, no map tile server.
//
// countryCounts: Array<{ countryCode: string, country: string, count: number }>
// onCountryClick: optional callback when a bubble is clicked (e.g. to filter
//   the IP table by country)
// World map with drill-down zoom. Mike Bostock-style: click a country
// → smooth zoom to it. Click the ocean → zoom back to the world view.
// Mouse wheel and drag also work (powered by d3-zoom).
//
// Implementation:
//   - d3-geo for the projection (equal-earth) and path generation
//   - d3-zoom for the zoom/pan behavior
//   - topojson-client for TopoJSON → GeoJSON conversion
//   - React for state + rendering
//
// The d3-zoom transform {x, y, k} is stored in React state and applied
// to the inner <g> via the `transform` attribute. Transitions are done
// via d3.select().transition().call(zoom.transform, ...).
function WorldMap({ countryCounts, onCountryClick, highlightCode }: {
  countryCounts: Array<{ countryCode: string; country: string; count: number }>;
  onCountryClick?: (code: string) => void;
  highlightCode?: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [paths, setPaths] = useState<Array<{ id: string; name: string; d: string; iso: string; centroid: [number, number] | null; bbox: [[number, number], [number, number]] | null }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // d3-zoom's identity transform (no translation, no scale)
  const [transform, setTransform] = useState<{ x: number; y: number; k: number }>({ x: 0, y: 0, k: 1 });
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  // Theme awareness — ocean / borders / labels adapt to light vs dark
  const isDark = useIsDark();

  // Load the TopoJSON once
  useEffect(() => {
    let cancelled = false;
    fetch(WORLD_GEO_URL)
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then((topology: any) => {
        if (cancelled) return;
        const geo = feature(topology, topology.objects.countries) as any;
        // Equal-earth projection — looks like a real world map, not
        // a rectangle. Scale 165 + translate [500, 250] centers it in
        // a 1000x500 viewBox.
        const projection = geoEqualEarth()
          .scale(165)
          .translate([500, 250]);
        const pathGen = geoPath(projection);
        // Build the path list, enriched with bbox + centroid for each country.
        // We need these for click-to-zoom (compute the target transform).
        const out = geo.features
          .map((f: any, i: number) => {
            const name: string = f.properties.name ?? '';
            const iso = COUNTRY_NAME_TO_ISO[name] || '';
            const d = pathGen(f) || '';
            if (!d) return null;
            // Centroid in screen coords (for bubble overlay)
            const c = pathGen.centroid(f);
            // Bounding box in screen coords (for click-to-zoom)
            const bb = pathGen.bounds(f);
            return {
              id: f.id ?? name ?? `geo-${i}`,
              name,
              d,
              iso,
              centroid: c && isFinite(c[0]) ? [c[0], c[1]] as [number, number] : null,
              bbox: bb ? [bb[0], bb[1]] as [[number, number], [number, number]] : null,
            };
          })
          .filter(Boolean) as any[];
        setPaths(out);
      })
      .catch(e => { if (!cancelled) setError(String(e)); });
    return () => { cancelled = true; };
  }, []);

  // Attach d3-zoom behavior to the SVG once it exists
  useEffect(() => {
    if (!svgRef.current) return;
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 8])  // min: world view, max: 8x zoom
      .on('zoom', (event: any) => {
        // User-driven zoom/pan (mouse wheel, drag) — update React state
        // so the <g transform="..."> reflects the new position.
        // event is typed `any` because d3-zoom ships without bundled
        // type defs (see declarations.d.ts). The fields we read
        // (event.transform.{x,y,k}) exist on every D3 zoom event.
        setTransform({ x: event.transform.x, y: event.transform.y, k: event.transform.k });
        // If user drags back to scale 1, clear the "active country" indicator
        if (event.transform.k <= 1.05) setActiveFeature(null);
      });
    // Click on the ocean (the background <rect>) → reset zoom
    z.on('end', () => {
      // No-op here; we use a separate handler on the rect
    });
    zoomBehaviorRef.current = z;
    select(svgRef.current).call(z);
    // Disable double-click default (which zooms in by 2x)
    select(svgRef.current).on('dblclick.zoom', null);
    return () => {
      select(svgRef.current!).on('.zoom', null);
    };
  }, [paths]);

  // Animate the zoom to a specific country (or back to world).
  //
  // `feat` is the same shape as the elements of the `paths` state array —
  // a single rendered country. We type it with an explicit interface
  // (matching the useState<...> declaration a few lines above) rather
  // than the previous `typeof paths extends Array<infer T> ? T : never |
  // null` pattern, which collapsed to `never` when `paths` was null at
  // the time the function was first evaluated and then made every
  // `feat.bbox` / `feat.name` access error with "property does not
  // exist on type 'never'".
  interface CountryFeature {
    id: string;
    name: string;
    d: string;
    iso: string;
    centroid: [number, number] | null;
    bbox: [[number, number], [number, number]] | null;
  }
  function zoomToFeature(feat: CountryFeature | null) {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    if (!feat || !feat.bbox) {
      // Reset to world view
      select(svgRef.current).transition().duration(750).call(
        zoomBehaviorRef.current.transform,
        zoomIdentity
      );
      setActiveFeature(null);
      return;
    }
    const [[x0, y0], [x1, y1]] = feat.bbox;
    const dx = x1 - x0;
    const dy = y1 - y0;
    // Compute scale: fit the country's bbox into the viewport with padding.
    // padding = 0.4 → leaves 40% room around the country.
    const padding = 0.4;
    const scale = Math.max(1, Math.min(8,
      Math.min(1000 / (dx * (1 + padding)), 500 / (dy * (1 + padding)))
    ));
    // Center the country's bbox
    const tx = 500 - (scale * (x0 + x1)) / 2;
    const ty = 250 - (scale * (y0 + y1)) / 2;
    select(svgRef.current).transition().duration(750).call(
      zoomBehaviorRef.current.transform,
      zoomIdentity.translate(tx, ty).scale(scale)
    );
    setActiveFeature(feat.name);
  }

  // Reset to world view (called from the "back" button and from the rect)
  function resetZoom() {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    select(svgRef.current).transition().duration(750).call(
      zoomBehaviorRef.current.transform,
      zoomIdentity
    );
    setActiveFeature(null);
  }

  const max = countryCounts.reduce((m, c) => Math.max(m, c.count), 1);
  // Build a Set of country codes we have data for (for quick lookup in render)
  const codeToCount: Record<string, number> = {};
  countryCounts.forEach(c => { codeToCount[c.countryCode] = c.count; });
  const codeToName: Record<string, string> = {};
  countryCounts.forEach(c => { codeToName[c.countryCode] = c.country; });

  if (countryCounts.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm">
        No country data yet — run a historical query to see the map.
      </div>
    );
  }

  // Helper: should a country be labeled? Only when it's the active
  // feature OR when zoomed in enough to make labels readable.
  const showLabels = transform.k > 1.5;

  return (
    <div className="relative w-full" style={{ aspectRatio: '2 / 1' }}>
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
          <div className="text-center text-sm text-destructive">
            Failed to load world map: {error}
            <br />
            <span className="text-muted-foreground text-xs">Place world-110m.json in your public/ directory</span>
          </div>
        </div>
      )}
      {!paths && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-sm text-muted-foreground">Loading world map…</div>
        </div>
      )}
      {/* Back / reset button — visible only when zoomed in */}
      {transform.k > 1.05 && (
        <button
          onClick={resetZoom}
          className="absolute top-2 left-2 z-10 px-2 py-1 bg-card border border-border rounded text-xs hover:bg-muted/50 flex items-center gap-1"
        >
          ← Back to world
        </button>
      )}
      {/* Zoom level indicator */}
      <div className="absolute bottom-2 right-2 z-10 px-2 py-1 bg-card/80 border border-border rounded text-[10px] text-muted-foreground font-mono">
        {Math.round(transform.k * 100)}%
      </div>
      <svg
        ref={svgRef}
        viewBox="0 0 1000 500"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ cursor: 'grab' }}
      >
        {/* Ocean — click resets the zoom */}
        <rect
          x="0"
          y="0"
          width={1000}
          height={500}
          fill="hsl(var(--background))"
          onClick={resetZoom}
        />
        {/* Zoomable layer — countries + bubbles, all transformed together.
            All theme-aware colors come from CSS variables so they adapt to
            light/dark mode automatically (hsl(var(--xxx)) resolves to the
            current value of the variable, which changes with the .dark class). */}
        <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
          {/* Country paths */}
          {paths?.map(p => {
            const isAttacker = p.iso && codeToCount[p.iso] !== undefined;
            const isHighlighted = highlightCode && p.iso === highlightCode;
            const isActive = activeFeature === p.name;
            // Country fill: light red gradient via countryRedScale (always lighter
            // than the bubble's redScale so the bubble stands out visually).
            // Country stroke: matches the fill but slightly darker.
            // Bubble (rendered separately below) is a solid red so the country
            // and bubble can never be the same color.
            const baseFill = isAttacker
              ? countryRedScale(codeToCount[p.iso] / max, isDark)
              : isHighlighted || isActive
                ? 'hsl(var(--accent))'
                : 'hsl(var(--muted))';
            // Stroke: use hardcoded theme-aware colors because some shadcn
            // themes set --border == --muted in dark mode, making the stroke
            // invisible. In light mode the stroke is a medium-dark gray; in
            // dark mode it's a medium-light gray. Both are visible against
            // the muted fill in their respective themes.
            const baseStroke = isActive
              ? 'hsl(var(--primary))'
              : isHighlighted
                ? 'hsl(var(--accent))'
                : isAttacker
                  ? redScale(codeToCount[p.iso] / max, isDark)
                  : (isDark ? 'hsl(0, 0%, 55%)' : 'hsl(0, 0%, 65%)');
            // Thinner borders — non-attacker countries are subtle context,
            // attackers get a thin red outline, active (clicked) stays bold.
            const strokeWidth = isActive ? 1.2 / transform.k : isAttacker ? 0.35 : 0.2;
            return (
              <g key={p.id}>
                <path
                  d={p.d}
                  fill={baseFill}
                  stroke={baseStroke}
                  strokeWidth={strokeWidth}
                  className={isAttacker ? 'cursor-pointer' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAttacker) {
                      zoomToFeature(p);
                      onCountryClick?.(p.iso);
                    } else {
                      zoomToFeature(p);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (isAttacker) {
                      e.currentTarget.setAttribute('fill', countryRedScale(Math.min(1, codeToCount[p.iso] / max + 0.1), isDark));
                    } else {
                      e.currentTarget.setAttribute('fill', 'hsl(var(--accent))');
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.setAttribute('fill', baseFill);
                  }}
                />
                <title>
                  {p.iso
                    ? `${p.name} (${p.iso})${isAttacker ? ` — ${codeToCount[p.iso]} attack${codeToCount[p.iso] === 1 ? '' : 's'}` : ''}`
                    : p.name}
                </title>
              </g>
            );
          })}
          {/* Attack bubbles — one per attacker country, positioned at lat/lng centroid.
              Inside the zoomable group, so they zoom along with the country they're on. */}
          {paths?.filter(p => p.centroid && codeToCount[p.iso]).map(p => {
            const count = codeToCount[p.iso];
            const t = count / max;
            // Bubble color: SOLID red (single color, not a gradient).
            // The size of the bubble shows the count, so the color doesn't
            // need to vary. A single solid red contrasts cleanly with the
            // lighter country fill (which uses countryRedScale).
            const fill = isDark ? 'hsl(0, 85%, 58%)' : 'hsl(0, 82%, 48%)';
            const stroke = isDark ? 'hsl(0, 90%, 70%)' : 'hsl(0, 80%, 35%)';
            // Bubble radius — 40% smaller than v30 (per user feedback).
            // Formula: r = (5 + log2(count+1) * 4) * 0.6
            //   count=1   → r = 3
            //   count=10  → r ≈ 11
            //   count=100 → r ≈ 15
            //   count=1000→ r ≈ 21
            // Counter-scaled by 1/transform.k so the bubble appears the
            // same size on screen regardless of zoom level.
            const r = (5 + Math.log2(count + 1) * 4) * 0.6;
            return (
              <g key={`bub-${p.id}`} transform={`translate(${p.centroid![0]}, ${p.centroid![1]}) scale(${1/transform.k})`}>
                <title>{p.name} ({p.iso}) — {count} attack{count === 1 ? '' : 's'}</title>
                <circle r={r * 1.6} fill={fill} fillOpacity="0.18" />
                <circle
                  r={r}
                  fill={fill}
                  fillOpacity="0.92"
                  stroke={stroke}
                  strokeWidth="1.5"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    zoomToFeature(p);
                    onCountryClick?.(p.iso);
                  }}
                />
                {r > 5 && (
                  <text
                    textAnchor="middle"
                    dy={3}
                    fontSize={Math.min(11, r * 0.9)}
                    fontWeight="bold"
                    fill="#fff"
                    style={{ pointerEvents: 'none' }}
                  >
                    {count}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function fmtUptime(seconds: number) {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
}
function fmtMemoryMb(bytes: number) {
  if (!bytes) return '—';
  return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}
function fmtTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString();
  } catch { return iso; }
}

// "5 min ago" / "2 h ago" / "yesterday" / "3 d ago" — for the Overview's
// "Last attack" card. Falls back to a date string for anything > 7 days.
function fmtTimeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

// Format a full log line by converting the leading ISO timestamp to local time.
// Log format: "<ISO> | <user> | <action: ip> | <reason>"
//   2026-08-04T11:42:46.220Z | user@host | NGINX DENY ADDED: 1.2.3.4 | AUTO-BLOCKED: 97%
// Becomes:
//   2026-08-04 14:42:46 (Moscow) | user@host | NGINX DENY ADDED: 1.2.3.4 | AUTO-BLOCKED: 97%
function fmtLogLine(line: string): string {
  const m = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)\s*\|/);
  if (!m) return line;
  const iso = m[1];
  const d = new Date(iso);
  if (isNaN(d.getTime())) return line;
  // YYYY-MM-DD HH:MM:SS in local TZ (no milliseconds — matches log file convention)
  const pad = (n: number) => String(n).padStart(2, '0');
  const local = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  return line.replace(iso, local);
}

export default function OpsecPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Auxiliary lists (refreshed alongside snapshot)
  const [blockedIps, setBlockedIps] = useState<any[]>([]);
  const [denyIps, setDenyIps] = useState<string[]>([]);
  const [whitelistedIps, setWhitelistedIps] = useState<any[]>([]);
  const [blockLog, setBlockLog] = useState<string>('');
  const [denyLog, setDenyLog] = useState<string>('');
  const [nginxDenyLog, setNginxDenyLog] = useState<string>(''); // dedicated feed for Recently Auto-Blocked

  // ---- Historical view state ----
  // Date range defaults to last 12h. The user can widen to 7d / 30d
  // via the preset buttons — each preset also auto-triggers a fresh query
  // so the data updates immediately. 12h is the sweet spot for the
  // Attack Timeline table: enough history to spot a pattern, short
  // enough to read at a glance (12 rows vs 24), and it stays entirely
  // within the active nginx log so the backend cap never bites.
  const [histStart, setHistStart] = useState<string>(() => {
    const d = new Date(Date.now() - 12 * 60 * 60 * 1000);
    return d.toISOString().substring(0, 16); // "YYYY-MM-DDTHH:MM"
  });
  const [histEnd, setHistEnd] = useState<string>(() => {
    return new Date().toISOString().substring(0, 16);
  });
  const [histIp, setHistIp] = useState<string>('');
  const [histPath, setHistPath] = useState<string>('');
  const [histData, setHistData] = useState<any | null>(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState<string | null>(null);
  // Timestamp for the "Last updated" indicator next to the historical
  // header. Updated by Run query button, preset buttons, and the
  // window-focus refresh. Declared here (next to histData etc.) so
  // the useEffect below can reference it without hitting TDZ.
  const [histLastRefresh, setHistLastRefresh] = useState<Date | null>(null);
  // Drill-down modal: per-IP event list
  const [histDrillIp, setHistDrillIp] = useState<string | null>(null);
  // Drill-down modal: events for the drilled IP (fetched via a separate query
  // with the IP filter so we get ALL events for that IP, not just the truncated
  // set from the main query).
  const [histDrillData, setHistDrillData] = useState<any[] | null>(null);
  const [histDrillLoading, setHistDrillLoading] = useState(false);
  // Events-table filters (applied client-side to histData.events)
  // - histStatusFilter: "all" | "blocked" | "not-blocked" | "whitelisted" | "not-whitelisted"
  // - histReasonFilter: "all" | or one of the unique reason strings
  const [histStatusFilter, setHistStatusFilter] = useState<string>('all');
  const [histReasonFilter, setHistReasonFilter] = useState<string>('all');
  // Country filter (top IPs only — events don't carry country data to keep
  // the response small)
  const [histCountryFilter, setHistCountryFilter] = useState<string>('all');
  // Full-size world map modal
  const [mapOpen, setMapOpen] = useState<boolean>(false);
  // Rate-limit velocity detection. Polled every 30s. Each row has the
  // current rate, baseline rate, ratio, and suggested action (AUTO_BLOCK
  // when the spike is severe enough to block silently, MANUAL otherwise).
  const [velocityData, setVelocityData] = useState<any | null>(null);
  const [velocityLoading, setVelocityLoading] = useState<boolean>(false);
  const [velocityError, setVelocityError] = useState<string | null>(null);
  // Helper: apply the current filter selections to a raw events array.
  // Returns the same array if no filter is active (avoid a useless clone).
  // NOTE: the `filteredHistEvents` useMemo that uses this function is declared
  // LATER, after `denyIpSet` and `whitelistedIpSet` are defined. The function
  // itself is fine to define here because the consts are referenced lazily
  // (only when the function is called), but the useMemo call would TDZ-fail
  // if it were called here.
  function applyHistFilters(events: any[]): any[] {
    if (!events) return [];
    if (histStatusFilter === 'all' && histReasonFilter === 'all' && histCountryFilter === 'all') return events;
    return events.filter((e: any) => {
      if (histStatusFilter === 'blocked' && !denyIpSet.has(e.ip)) return false;
      if (histStatusFilter === 'not-blocked' && denyIpSet.has(e.ip)) return false;
      if (histStatusFilter === 'whitelisted' && !whitelistedIpSet.has(e.ip)) return false;
      if (histStatusFilter === 'not-whitelisted' && whitelistedIpSet.has(e.ip)) return false;
      if (histReasonFilter !== 'all' && e.reason !== histReasonFilter) return false;
      if (histCountryFilter !== 'all' && e.countryCode !== histCountryFilter) return false;
      return true;
    });
  }

  // Filters for suspicious table
  // Defaults: filterStatus='all' shows everything (including blocked);
  // hideMyIp hides the admin's own IP; hideBlocked now defaults to
  // FALSE so the table isn't empty when most attackers are already
  // blocked. Previously the default was true (matching the EJS), but
  // on a site with thousands of daily attacks that meant the table
  // appeared empty by default — every recent attacker was already in
  // blockedIpSet/denyIpSet. The user can still check "Hide blocked"
  // to declutter.
  const [filterStatus, setFilterStatus] = useState<'all' | 'not_blocked' | '403' | 'suspicious'>('all');
  const [filterIp, setFilterIp] = useState('');
  const [hideMyIp, setHideMyIp] = useState(true);
  const [hideBlocked, setHideBlocked] = useState(false);
  const [hideWhitelisted, setHideWhitelisted] = useState(true);

  // AbuseIPDB confidence-of-abuse scores, keyed by IP. Values:
  //   undefined  = not yet fetched
  //   null       = fetch failed (e.g. API key not set, rate limited)
  //   {score, totalReports, isp, countryCode, ...} = fetched
  const [abuseScores, setAbuseScores] = useState<Record<string, any>>({});
  const [abuseEnabled, setAbuseEnabled] = useState<boolean>(true); // becomes false if API key missing
  const [abuseBulkBusy, setAbuseBulkBusy] = useState<boolean>(false);

  // Track IPs already auto-blocked (95-100% confidence, no user intervention)
  // so we don't try to re-block them on every render.
  const [autoBlockedIps, setAutoBlockedIps] = useState<Set<string>>(new Set());

  // Track IPs already instant-blocked for recon probes (WP/.env/.git/xmlrpc/.php)
  // regardless of AbuseIPDB score. Blocked in BOTH UFW and nginx.
  const [instantBlockedIps, setInstantBlockedIps] = useState<Set<string>>(new Set());

  // Pause/resume switch for the auto-blocker. Off by default? No — ON.
  // Safety switch for when AbuseIPDB misbehaves or during maintenance.
  const [autoBlockEnabled, setAutoBlockEnabled] = useState<boolean>(true);

  // "Removed" review queue IPs — permanent dismissal that survives page reloads.
  // Stored in localStorage so the user doesn't have to re-dismiss the same
  // false positives every time they reload the page.
  const [removedReviewIps, setRemovedReviewIps] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('opsec-removed-review-ips');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      localStorage.setItem('opsec-removed-review-ips', JSON.stringify(Array.from(removedReviewIps)));
    } catch { /* quota exceeded — ignore */ }
  }, [removedReviewIps]);

  // PERSISTENT Pending Decision queue.
  // Accumulates 80-95% IPs from EVERY snapshot (not just current). When a
  // snapshot refresh brings in a 80-95% IP that we've never seen, it gets
  // added to this map. The IP stays here until the user acts on it
  // (Block / WL / Remove) or until it's auto-blocked.
  //
  // localStorage shape: { [ip]: { firstSeen, lastSeen, confidence, path, reason, count } }
  type PendingEntry = { firstSeen: number; lastSeen: number; confidence: number; path: string; reason: string; count: number };
  const [pendingReviewIps, setPendingReviewIps] = useState<Record<string, PendingEntry>>(() => {
    try {
      const raw = localStorage.getItem('opsec-pending-review-ips');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('opsec-pending-review-ips', JSON.stringify(pendingReviewIps));
    } catch { /* quota — ignore */ }
  }, [pendingReviewIps]);

  // Ref tracking when each abuse score was fetched, so we can re-fetch
  // stale ones on each snapshot refresh without burning the API budget
  // (backend has its own 1h cache, but this avoids even the round-trip).
  const scoreTimestampsRef = useRef<Record<string, number>>({});

  // Forms
  const [blockIp, setBlockIp] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [denyCidr, setDenyCidr] = useState('');
  const [whitelistIp, setWhitelistIp] = useState('');
  const [whitelistReason, setWhitelistReason] = useState('');

  // Search filters
  const [blockedSearch, setBlockedSearch] = useState('');
  const [denySearch, setDenySearch] = useState('');

  // Action states (per-button busy)
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [actionMsg, setActionMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Report modal
  const [reportRow, setReportRow] = useState<any | null>(null);
  // Override the auto-suggested category from the dropdown in the report modal
  const [reportCategory, setReportCategory] = useState<string | null>(null);
  // Override the comment text when the user picks a different category
  const [reportCommentOverride, setReportCommentOverride] = useState<string | null>(null);
  const [logExpanded, setLogExpanded] = useState<{ [k: string]: boolean }>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [snap, blocked, denied, whitelisted, blog, dlog, ndlog] = await Promise.all([
        api.opsecGetSnapshot(),
        api.opsecGetBlockedIps().catch(() => ({ blocked: [] })),
        api.opsecGetDenyIps().catch(() => ({ denied: [] })),
        api.opsecGetWhitelistedIps().catch(() => ({ whitelisted: [] })),
        api.opsecGetBlockLog().catch(() => ({ log: '' })),
        api.opsecGetDenyLog().catch(() => ({ log: '' })),
        api.opsecGetNginxDenyLog().catch(() => ({ log: '' })),
      ]);
      setSnapshot(snap);
      setBlockedIps(blocked?.blocked || []);
      setDenyIps(denied?.denied || []);
      setWhitelistedIps(whitelisted?.whitelisted || []);
      setBlockLog(blog?.log || '');
      setDenyLog(dlog?.log || '');
      setNginxDenyLog(ndlog?.log || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load OPSEC data');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- 60s polling: refresh snapshot + abuse scores together ----
  // The page is meant to be left open in the background as a monitoring
  // dashboard. Every 60s we re-fetch the snapshot, which updates
  // filteredSuspicious → triggers the abuse-score useEffect above →
  // scores older than 5 min are re-fetched → auto-blocker re-evaluates.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // ---- 1h polling: auto-block summary (for FCM notification) ----
  // Backend returns the count of AUTO-BLOCKED entries in the last 60 min.
  // The backend is responsible for sending the FCM push notification;
  // the frontend just polls and surfaces the count. The user can also
  // wire up the actual FCM call in the backend endpoint (see TODO comment
  // in opsec.js /auto-block-summary route).
  const [autoBlockSummary, setAutoBlockSummary] = useState<{ autoCount: number; manualCount: number; reconCount: number; recentAutoIps: string[]; recentReconIps: string[] } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const fetchSummary = () => {
      // 24h window so the Overview card matches the "Recently Auto-Blocked"
      // table below (which shows last 24h). The 60m version is still used
      // in the header subtitle — kept as a comment so we know the tradeoff.
      api.opsecAutoBlockSummary(24 * 60)
        .then((s) => { if (!cancelled) setAutoBlockSummary({ autoCount: s.autoCount, manualCount: s.manualCount, reconCount: s.reconCount || 0, recentAutoIps: s.recentAutoIps, recentReconIps: s.recentReconIps || [] }); })
        .catch(() => { /* silent */ });
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 60 * 60 * 1000); // 1 hour
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ---- 30s polling: rate-limit velocity detection ----
  // The backend reads the last 50k nginx access log lines, buckets requests
  // by IP and minute, and flags IPs whose current rate is >= 5x their
  // baseline. This is independent of AbuseIPDB — a clean IP that's
  // currently hammering the server still gets flagged.
  const fetchVelocity = useCallback(async () => {
    setVelocityLoading(true);
    try {
      const data = await api.opsecVelocity();
      setVelocityData(data);
      setVelocityError(null);
    } catch (e: any) {
      setVelocityError(e?.message || 'Failed to load velocity data');
    } finally {
      setVelocityLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchVelocity();
    const id = setInterval(fetchVelocity, 30_000);
    return () => clearInterval(id);
  }, [fetchVelocity]);

  // ---- 5-min polling: top countries last 24h (for the Overview card) ----
  // The snapshot's topIPs comes from the last 2000 nginx log lines — that's
  // NOT a fixed time window. For the "Top Attacker Country" card on the
  // Overview, we want a strict "last 24 hours" view, so we hit the
  // historical endpoint with start=24h_ago. We only use the topCountries
  // field from the response (the rest is wasted bandwidth but the query
  // is cheap).
  const [topCountries24h, setTopCountries24h] = useState<Array<{ country: string; countryCode: string; count: number }>>([]);
  const fetchTopCountries24h = useCallback(async () => {
    try {
      const data = await api.opsecHistoricalAttacks({
        start: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        end: new Date().toISOString(),
        limit: 100,  // small limit; we only want topCountries aggregation
      });
      setTopCountries24h(data?.topCountries || []);
    } catch (e: any) {
      // Silent — the card just shows "no data" if the query fails
    }
  }, []);
  useEffect(() => {
    fetchTopCountries24h();
    const id = setInterval(fetchTopCountries24h, 5 * 60 * 1000); // 5 min
    return () => clearInterval(id);
  }, [fetchTopCountries24h]);

  // ---- Historical view: shared query helper ----
  // Single source of truth for "run a historical query". Used by:
  //   - the "Run query" button (manual)
  //   - the Last 24h / 7d / 30d preset buttons (auto-trigger on click)
  //   - the 60s polling useEffect
  //   - the tab-open auto-refresh useEffect
  // Adaptive maxLines: 24h queries read 100K lines (fast, ~1s), 7d reads
  // 500K, 30d+ reads 2M. Without this every query would read 2M lines
  // even for a 24h range, making short queries wastefully slow.
  const runHistoricalQuery = useCallback(async (params: {
    start?: string;
    end?: string;
    ip?: string;
    path?: string;
    limit?: number;
    maxLines?: number;
  } = {}) => {
    setHistLoading(true);
    setHistError(null);
    try {
      // Compute maxLines from the requested range if not explicit.
      // ~2K lines/hour is the right ballpark for the user's actual
      // traffic (a few req/min on the API + dashboard). Hard cap at
      // 200K so a wide range can't blow up memory. The user explicitly
      // asked for query-based: no polling, no heavy buffers.
      const startStr = params.start ?? (histStart ? new Date(histStart).toISOString() : new Date(Date.now() - 7 * 86400e3).toISOString());
      const endStr = params.end ?? (histEnd ? new Date(histEnd).toISOString() : new Date().toISOString());
      const startMs = new Date(startStr).getTime();
      const endMs = new Date(endStr).getTime();
      const hours = Math.max(1, (endMs - startMs) / 3600e3);
      const adaptiveMaxLines = params.maxLines ?? Math.min(200_000, Math.max(50_000, Math.ceil(hours * 2_000)));
      const data = await api.opsecHistoricalAttacks({
        start: startStr,
        end: endStr,
        ip: params.ip ?? (histIp || undefined),
        path: params.path ?? (histPath || undefined),
        limit: params.limit ?? 2000,
        maxLines: adaptiveMaxLines,
      });
      setHistData(data);
      setHistLastRefresh(new Date());
    } catch (e: any) {
      setHistError(e?.message || 'Query failed');
    } finally {
      setHistLoading(false);
    }
  }, [histStart, histEnd, histIp, histPath]);

  // ---- Tab navigation ----
  // 5 tabs: Overview / Live Threats / Block Lists / Historical / Logs.
  // Active tab is persisted in localStorage AND the URL hash, so the user
  // can deep-link to a specific view (e.g. #historical).
  const TABS = [
    { key: 'overview',   label: 'Overview',    icon: '📊' },
    { key: 'threats',    label: 'Live Threats', icon: '⚡' },
    { key: 'lists',      label: 'Block Lists',  icon: '📋' },
    { key: 'historical', label: 'Historical',   icon: '📈' },
    { key: 'logs',       label: 'Logs',         icon: '📜' },
  ];
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window === 'undefined') return 'overview';
    // URL hash wins (deep link)
    const hash = window.location.hash.replace('#', '');
    if (TABS.some(t => t.key === hash)) return hash;
    // Then localStorage (last view)
    try {
      const stored = localStorage.getItem('opsec-active-tab');
      if (stored && TABS.some(t => t.key === stored)) return stored;
    } catch (_) { /* localStorage may be disabled */ }
    return 'overview';
  });
  useEffect(() => {
    try { localStorage.setItem('opsec-active-tab', activeTab); } catch (_) {}
    if (typeof window !== 'undefined' && window.location.hash !== '#' + activeTab) {
      // Use replaceState so we don't fill browser history with every tab click
      window.history.replaceState(null, '', '#' + activeTab);
    }
  }, [activeTab]);

  // ---- Historical view auto-refresh on tab open ----
  // The histEnd default is set when the component mounts. If the user keeps
  // the page open for hours, histEnd gets stale and the timeline truncates
  // (e.g. shows up to 18:00 when it's actually 00:00 next day). When the
  // user opens the Historical tab, bump histEnd to "now" and re-query.
  // We only auto-fire if the cached data is older than 5 minutes; if the
  // user has just been browsing, we leave their existing query alone.
  // (The [activeTab] dep means this only runs on tab CHANGE, not on
  // every histData update. Declared AFTER activeTab so the closure
  // captures it after initialization — see commit history for the TDZ
  // bug this prevented.)
  useEffect(() => {
    if (activeTab !== 'historical') return;
    setHistEnd(new Date().toISOString().substring(0, 16));
    const lastFetch = (histData as any)?.range?.end ? new Date((histData as any).range.end).getTime() : 0;
    const staleMs = Date.now() - lastFetch;
    if (!histData || staleMs > 5 * 60 * 1000) {
      // Honor the user's chosen start (Last 24h / 7d / 30d preset, or
      // a typed-in value). Falls back to 7d if no value is set yet.
      const start = histStart
        ? new Date(histStart).toISOString()
        : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      runHistoricalQuery({
        start,
        end: new Date().toISOString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ---- Historical view: NO polling (was 60s, removed) ----
  // The historical endpoint reads up to ~50 MB of nginx log per query
  // and the V8 heap holds onto that buffer long-term, so a 60s poll
  // kept the process pinned at ~150 MB resident. The user's request
  // is "query-based when needed" — the historical view is for looking
  // at the past, not live monitoring, so polling is overkill.
  //
  // The data refreshes only when:
  //   1. The user opens the Historical tab (auto-refresh useEffect above)
  //   2. The user clicks "Run query"
  //   3. The user clicks a Last 24h / 7d / 30d preset (auto-triggers)
  //   4. The user comes back to the tab after >5 min away (focus refresh)
  //
  // For live monitoring, the Overview tab polls the snapshot every 60s
  // (cheap, 50K lines, ~30 MB). The Live Threats tab polls velocity
  // every 30s (also cheap). Historical is heavy and should be on-demand.
  const histStartRef = useRef(histStart);
  useEffect(() => { histStartRef.current = histStart; }, [histStart]);

  // Window-focus refresh: if the user switches to another tab/app and
  // comes back to the dashboard after >5 min, refresh the historical
  // data once. This is cheaper than polling (only fires on actual focus
  // changes) but keeps the data from being hours stale.
  useEffect(() => {
    if (activeTab !== 'historical') return;
    let lastFocusRefresh = 0;
    const onFocus = () => {
      const now = Date.now();
      // Only refresh if it's been >5 min since the last focus refresh
      // (or the last data fetch) AND the data is older than 5 min.
      const dataAge = histLastRefresh ? now - histLastRefresh.getTime() : Infinity;
      if (now - lastFocusRefresh < 5 * 60_000) return;
      if (dataAge < 5 * 60_000) return;
      lastFocusRefresh = now;
      const end = new Date().toISOString();
      setHistEnd(end.substring(0, 16));
      const start = histStartRef.current
        ? new Date(histStartRef.current).toISOString()
        : new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      runHistoricalQuery({ start, end });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [activeTab, runHistoricalQuery, histLastRefresh]);

  // Hamburger menu state (top right). Click outside to close.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  function flash(kind: 'ok' | 'err', text: string) {
    setActionMsg({ kind, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function doAction(key: string, fn: () => Promise<any>) {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await fn();
      flash('ok', r?.message || 'Done');
      await load();
    } catch (e: any) {
      flash('err', e?.message || 'Action failed');
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  }

  // ---- Suspicious filtering ----
  const suspicious = snapshot?.accessStats?.suspiciousPatterns || [];
  const blockedIpSet = useMemo(() => new Set(blockedIps.map((b: any) => b.ip)), [blockedIps]);
  const denyIpSet = useMemo(() => new Set(denyIps), [denyIps]);
  const whitelistedIpSet = useMemo(() => new Set(whitelistedIps.map((w: any) => w.ip)), [whitelistedIps]);
  const myIp = snapshot?.clientIP || '';

  // Memoized filtered events for the count display + table render.
  // Recomputes only when the source events, the deny/wl sets, or the
  // filter selections change. Avoids running the filter 4 times per render
  // (once in the header, once in the table, etc.). Must be declared AFTER
  // `denyIpSet` and `whitelistedIpSet` because useMemo evaluates its
  // factory synchronously on first render — putting it earlier would
  // TDZ-fail with "Cannot access 'denyIpSet' before initialization".
  const filteredHistEvents = useMemo(
    () => applyHistFilters(histData?.events || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [histData, denyIpSet, whitelistedIpSet, histStatusFilter, histReasonFilter, histCountryFilter]
  );

  // ---- Theme toggle (light / dark) ----
  // Tailwind's dark mode is controlled by the `dark` class on the
  // root <html> element. We persist the user's choice in localStorage
  // and apply it on mount. If no preference, follow the OS setting
  // (matchMedia('prefers-color-scheme: dark')).
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const saved = localStorage.getItem('opsec-theme');
      if (saved === 'light' || saved === 'dark') return saved;
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch { return 'light'; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('opsec-theme', theme);
    } catch { /* quota — ignore */ }
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [theme]);

  const filteredSuspicious = useMemo(() => {
    return suspicious.filter((row: any) => {
      if (filterStatus !== 'all') {
        if (filterStatus === 'not_blocked' && (blockedIpSet.has(row.ip) || denyIpSet.has(row.ip))) return false;
        const s = String(row.status);
        if (filterStatus === '403' && s !== '403') return false;
        if (filterStatus === 'suspicious' && s !== 'suspicious') return false;
      }
      if (filterIp && !String(row.ip).includes(filterIp)) return false;
      if (hideMyIp && row.ip === myIp) return false;
      if (hideBlocked && (blockedIpSet.has(row.ip) || denyIpSet.has(row.ip))) return false;
      return true;
    });
  }, [suspicious, filterStatus, filterIp, hideMyIp, hideBlocked, blockedIpSet, denyIpSet, myIp]);

  // Hit count per IP across the ENTIRE suspicious log (not just the
  // filtered set). This way the "Hits" column shows the real attack
  // volume for that IP even if the user has filtered some events out.
  // Same key shape as topIPs so a single number reflects all activity.
  const hitsByIp = useMemo(() => {
    const m: Record<string, number> = {};
    for (const row of suspicious) {
      m[row.ip] = (m[row.ip] || 0) + 1;
    }
    return m;
  }, [suspicious]);

  // Merge any 80-94% IPs from the current snapshot into the persistent
  // pending-queue. This runs every time the snapshot updates (60s polling).
  // 95-100% IPs are AUTO-BLOCKED and never enter this queue.
  // IPs that have been seen before get their lastSeen/confidence/path updated.
  // New IPs are added. IPs no longer at 80-94% are NOT removed (the user
  // may want to review them even if AbuseIPDB score dropped).
  // MUST be placed AFTER `filteredSuspicious` is declared (TDZ).
  useEffect(() => {
    if (filteredSuspicious.length === 0) return;
    const now = Date.now();
    const updates: Record<string, any> = {};
    let changed = false;
    filteredSuspicious.forEach((row: any) => {
      const s = abuseScores[row.ip];
      if (!s) return;
      const c = s.abuseConfidenceScore || 0;
      if (c < 80 || c >= 95) return; // 95%+ is auto-blocked; <80% is ignored
      const existing = pendingReviewIps[row.ip];
      if (!existing) {
        updates[row.ip] = {
          firstSeen: now,
          lastSeen: now,
          confidence: c,
          path: row.path || '',
          reason: row.reason || '',
          count: 1,
        };
        changed = true;
      } else if (
        existing.confidence !== c ||
        existing.path !== (row.path || '') ||
        existing.reason !== (row.reason || '')
      ) {
        updates[row.ip] = {
          ...existing,
          lastSeen: now,
          confidence: c,
          path: row.path || existing.path,
          reason: row.reason || existing.reason,
          count: existing.count + 1,
        };
        changed = true;
      }
    });
    if (changed) {
      setPendingReviewIps((prev) => ({ ...prev, ...updates }));
    }
    // Intentionally NOT depending on pendingReviewIps — we read it via ref-like
    // pattern (setState batches the prev). Including it would cause infinite loops.
  }, [filteredSuspicious, abuseScores, autoBlockEnabled]);

  // ---- AbuseIPDB confidence lookup (per visible IP) ----
  // Auto-fetch the abuse score for every IP that appears in the filtered
  // suspicious list. Cache per session. If the API key isn't configured
  // (server returns 503 with enabled:false), hide the column entirely.
  useEffect(() => {
    if (!abuseEnabled) return;
    const visibleIps = new Set<string>();
    filteredSuspicious.forEach((row: any) => visibleIps.add(row.ip));
    const FIVE_MIN = 5 * 60 * 1000;
    const now = Date.now();
    visibleIps.forEach((ip) => {
      const ts = scoreTimestampsRef.current[ip];
      const age = ts ? now - ts : Infinity;
      // Re-fetch if not yet fetched OR if older than 5 minutes.
      // (Failed fetches stored as null still re-fetch immediately.)
      if (abuseScores[ip] !== undefined && abuseScores[ip] !== null && age < FIVE_MIN) return;
      api.opsecAbuseIpdbCheck(ip)
        .then((r) => {
          if (r.enabled === false) {
            setAbuseEnabled(false);
            return;
          }
          if (r.success) {
            setAbuseScores((prev) => ({ ...prev, [ip]: r }));
          } else {
            setAbuseScores((prev) => ({ ...prev, [ip]: null }));
          }
          scoreTimestampsRef.current[ip] = Date.now();
        })
        .catch(() => {
          setAbuseScores((prev) => ({ ...prev, [ip]: null }));
        });
    });
  }, [filteredSuspicious, abuseEnabled]); // abuseScores intentionally omitted (write-only)

  // ---- Auto-block 95-100% confidence IPs (NO user intervention) ----
  // When a suspicious row's AbuseIPDB score lands in the 95-100% range and
  // it isn't already blocked/whitelisted/auto-handled, fire the bulk-block
  // API immediately. The autoBlockedIps set prevents re-firing on re-renders.
  useEffect(() => {
    if (!abuseEnabled || !autoBlockEnabled) return;
    const targets = filteredSuspicious
      .filter((row: any) => {
        const s = abuseScores[row.ip];
        if (!s) return false;
        const c = s.abuseConfidenceScore || 0;
        return c >= 95
          && !denyIpSet.has(row.ip)
          && !autoBlockedIps.has(row.ip);
      })
      .map((row: any) => row.ip);
    if (targets.length === 0) return;

    console.log('[opsec] auto-block firing for', targets);
    api.opsecAbuseIpdbBulkBlock(targets, 95, 'AUTO')
      .then((r) => {
        const blocked: string[] = (r.results || [])
          .filter((res: any) => res.ok)
          .map((res: any) => res.ip);
        if (blocked.length > 0) {
          setAutoBlockedIps((prev) => {
            const next = new Set(prev);
            blocked.forEach((ip) => next.add(ip));
            return next;
          });
          // Inline the toast logic — DO NOT depend on `flash` here.
          // `flash` is recreated on every render, which would cause this
          // useEffect to re-fire constantly and the toast to never settle.
          setActionMsg({ kind: 'ok', text: `🛡️ Auto-blocked ${blocked.length} IP(s) at ≥95% confidence` });
          setTimeout(() => setActionMsg(null), 6000);
          load(); // refresh deny list / snapshot
        }
      })
      .catch(() => { /* silent: will retry on next render */ });
  }, [filteredSuspicious, abuseScores, abuseEnabled, denyIpSet, autoBlockedIps, autoBlockEnabled, load]);

  // ---- Instant block: known recon paths (WP, .env, .git, xmlrpc, .php) ----
  // These are 100% malicious reconnaissance — block in BOTH UFW and nginx
  // immediately, regardless of AbuseIPDB score. Reasoning:
  //   - Site is NOT WordPress, so any wp-* access is unambiguous recon
  //   - .env/.git/config.json = credential / source-code theft attempts
  //   - xmlrpc = brute-force amplification vector
  //   - .php on a non-PHP site = automated vulnerability scanner
  // The instantBlockedIps set prevents re-firing on re-renders.
  // This runs INDEPENDENT of autoBlockEnabled — recon probes are always
  // blocked, no matter the abuse score or the auto-block toggle.
  useEffect(() => {
    if (suspicious.length === 0) return;
    // Match on path OR on the reason text the backend already labeled.
    // The reason text is more reliable because it covers subtle variants
    // (e.g. wp-config.php is labeled "WordPress admin access"). Also catches
    // generic recon paths the user confirmed are malicious: /login, /admin,
    // /phpmyadmin, /backup, /shell.php, /phpinfo, /api/auth, /console etc.
    const reconIps = suspicious
      .filter((row: any) => {
        if (instantBlockedIps.has(row.ip)) return false;
        if (denyIpSet.has(row.ip)) return false;
        if (autoBlockedIps.has(row.ip)) return false;
        const path = String(row.path || '').toLowerCase();
        const reason = String(row.reason || '').toLowerCase();
        // Path matches one of the known recon patterns
        return /wp-admin|wp-login|wp-content|wp-includes|wp-config|\.env|\.git|\.svn|\.hg|config\.json|\.htaccess|\.htpasswd|xmlrpc|\.php|phpmyadmin|\/pma|\/myadmin|\/administrator|\/admin(istrator)?\b|\/login\b|\/admin\.php|\/shell\.php|\/c99\.php|\/r57\.php|\/backup|\.bak\b|\.sql\b|\.dump\b|\/phpinfo|\/info\.php|\/api\/auth|\/oauth|\/api\/login|\/console\b|\/debug\b|\/test\.php/.test(path)
          // OR backend labeled it as one of the recon reasons
          || /wordpress|\.env|\.git|config\.json|xmlrpc|\.php|admin|login|phpmyadmin|backup|webshell|oauth|debug|phpinfo|svn|hg/.test(reason);
      })
      .map((row: any) => ({ ip: row.ip, path: row.path, reason: row.reason }));
    if (reconIps.length === 0) return;
    const ipList = reconIps.map((r: any) => r.ip);
    const firstReason = reconIps[0]?.reason || reconIps[0]?.path || 'Recon path probe';
    console.log('[opsec] recon-instant-block firing for', ipList);
    api.opsecReconInstantBlock(ipList, firstReason)
      .then((r) => {
        const blocked: string[] = (r.results || [])
          .filter((res: any) => res.ok && !res.alreadyBlocked)
          .map((res: any) => res.ip);
        if (blocked.length > 0) {
          setInstantBlockedIps((prev) => {
            const next = new Set(prev);
            blocked.forEach((ip) => next.add(ip));
            return next;
          });
          setActionMsg({
            kind: 'ok',
            text: `🚨 Instant-blocked ${blocked.length} IP(s) for recon probes (WP/.env/.git/xmlrpc/.php) — UFW + Nginx`,
          });
          setTimeout(() => setActionMsg(null), 6000);
          load();
        }
      })
      .catch(() => { /* silent */ });
  }, [suspicious, denyIpSet, autoBlockedIps, instantBlockedIps, load]);

  // ---- Bulk block 80-95% confidence IPs (user clicks button, confirms) ----
  async function bulkBlockHighConfidence() {
    const targets = filteredSuspicious
      .filter((row: any) => {
        const s = abuseScores[row.ip];
        if (!s) return false;
        const c = s.abuseConfidenceScore || 0;
        // When auto-block is paused (OFF), include 95%+ IPs too so the
        // admin can review them manually instead of being silently blocked.
        const inRange = autoBlockEnabled
          ? (c >= 80 && c < 95)
          : (c >= 80);
        return inRange && !denyIpSet.has(row.ip) && !autoBlockedIps.has(row.ip);
      })
      .map((row: any) => row.ip);
    if (targets.length === 0) { flash('ok', 'No IPs in 80%+ range in view'); return; }
    const rangeLabel = autoBlockEnabled ? '80-95%' : '80-100%';
    if (!confirm(`Block ${targets.length} IP(s) with ${rangeLabel} AbuseIPDB confidence in Nginx?\n\n${targets.join('\n')}`)) return;
    setAbuseBulkBusy(true);
    try {
      const r = await api.opsecAbuseIpdbBulkBlock(targets, 80, 'MANUAL');
      flash('ok', `Blocked ${r.blocked}, already-blocked ${r.alreadyBlocked}`);
      await load();
      // Refresh abuse scores for the just-blocked IPs
      setAbuseScores((prev) => {
        const next = { ...prev };
        r.results.forEach((res: any) => {
          if (res.ok) delete next[res.ip]; // re-fetch on next render
        });
        return next;
      });
    } catch (e: any) {
      flash('err', e?.message || 'Bulk block failed');
    } finally {
      setAbuseBulkBusy(false);
    }
  }

  // ---- Forms submit ----
  function submitBlock(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidIp(blockIp)) { flash('err', 'Invalid IPv4 address'); return; }
    doAction('submitBlock', () => api.opsecBlockIp(blockIp, blockReason || undefined));
    setBlockIp(''); setBlockReason('');
  }
  function submitDeny(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidCidr(denyCidr)) { flash('err', 'Invalid IPv4 or CIDR (e.g. 1.2.3.4 or 1.2.3.0/24)'); return; }
    doAction('submitDeny', () => api.opsecDenyIp(denyCidr));
    setDenyCidr('');
  }
  function submitWhitelist(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidIp(whitelistIp)) { flash('err', 'Invalid IPv4 address'); return; }
    doAction('submitWhitelist', () => api.opsecWhitelistIp(whitelistIp, whitelistReason || undefined));
    setWhitelistIp(''); setWhitelistReason('');
  }

  // ---- Threat intel check (eye icon in suspicious table) ----
  // The EJS opened 4 sites via staggered window.open() calls. That works on
  // desktop browsers but PWAs / mobile WebViews block the entire burst as
  // a popup storm. We now render the eye button as a real <a target="_blank">
  // pointing at AbuseIPDB's IP check page (the canonical first stop — the
  // other 3 sites link out from there). See the table row markup.
  // The legacy checkIP() function is kept in case anyone wires it back up
  // later (e.g. for desktop users where the 4-tab burst worked fine).

  // ---- Report comment (lifted from the EJS reportIP function, line-for-line) ----
  // The EJS pulls ALL suspicious rows for the same IP and builds a multi-line
  // comment with each row as an evidence line. Then it maps the aggregated
  // reasons to an AbuseIPDB category. Finally it truncates to 1024 chars.
  //
  // Since the PWA doesn't have a DOM table to query, we replicate by:
  //   1. Filtering the snapshot's suspiciousPatterns to rows with the same IP
  //   2. Building the same evidence-line format
  //   3. Running the same reason->category regex chain
  //   4. Truncating to 1024 chars
  // ---- Report comment (lifted from the EJS reportIP function, line-for-line) ----
  // The EJS pulls ALL suspicious rows for the same IP and builds a multi-line
  // comment with each row as an evidence line. Then it maps the aggregated
  // reasons to an AbuseIPDB category. Finally it truncates to 1024 chars.
  //
  // Since the PWA doesn't have a DOM table to query, we replicate by:
  //   1. Filtering the snapshot's suspiciousPatterns to rows with the same IP
  //   2. Building the same evidence-line format
  //   3. Running the same reason->category regex chain
  //   4. Truncating to 1024 chars
  //
  // The report below is structured like a real ops incident report:
  //   - Summary line (IP, count, time range, distinct patterns)
  //   - Suggested AbuseIPDB category WITH its numeric ID so the user can
  //     pick it on AbuseIPDB without re-typing
  //   - Evidence lines (one per suspicious request, deduped if path+status
  //     repeats within the same minute)
  //   - Footer with system context
  //
  // AbuseIPDB category IDs (from https://www.abuseipdb.com/categories):
  //   1  DNS Compromise           11 Email Spam          21 Fraud Orders
  //   2  DNS Poisoning            12 FTP Brute-Force     22 Hacking
  //   3  Fraud Calls              13 Generic/SSH         23 IoT Targeted
  //   4  DDoS Attack              14 HTTP Attack         24 Login Sys.
  //   5  Open Proxy               15 ICMP                25 Malware
  //   6  Web Spam                 16 Malicious File      26 Phishing
  //   7  Email Spam               17 Port Scan           27 SQL Injection
  //   8  VoIP                     18 Brute-Force         28 SSH
  //   9  Fraud VoIP               19 Spam                29 Web App Attack
  //  10  Bad Web Bot              20 DDoS Source         30 Bad Web Bot
  //  (multiple IDs may apply)
  const CATEGORY_NUM = {
    'Brute-Force': '18,22',
    'Port Scan': '17,14',
    'SQL Injection': '27,22',
    'Web App Attack': '29,22',
    'Bad Web Bot': '10,9',
    'SSH': '13,28',
    'FTP Brute-Force': '12,18',
    'DDoS Attack': '4,20',
    'Phishing': '26',
    'Email Spam': '7,11',
    'Open Proxy': '5',
    'Spoofing': '15',
    'Hacking': '22',
  };

  function buildReportComment(ip: string) {
    const allSuspicious: any[] = snapshot?.accessStats?.suspiciousPatterns || [];
    const rows = allSuspicious.filter((r: any) => r.ip === ip);

    // If for some reason the IP isn't in the snapshot (e.g. user came from
    // a single-row context), fall back to a one-line placeholder so the
    // modal still has something useful.
    if (rows.length === 0) {
      return {
        comment: `Suspicious activity detected from IP ${ip}.\n\nNo detailed evidence available — single-row context.\n\nReported via OPSEC monitoring.`,
        category: 'Hacking',
        categoryNum: CATEGORY_NUM['Hacking'],
      };
    }

    // Aggregate stats for the summary
    const reasons = new Set<string>();
    const paths = new Set<string>();
    let firstTime: string | null = null;
    let lastTime: string | null = null;
    const rawEvidence: string[] = [];
    rows.forEach((row: any) => {
      const time = row.time || '';
      const path = row.path || '';
      const status = String(row.status ?? '');
      const reason = row.reason || '';
      if (!firstTime || time < firstTime) firstTime = time;
      if (!lastTime  || time > lastTime)  lastTime  = time;
      if (reason) reasons.add(String(reason));
      if (path)  paths.add(path);
      rawEvidence.push(`- ${time} | ${status} | ${path} | ${reason}`);
    });

    // Dedupe evidence lines that repeat within the same minute — same
    // status + same path + same minute = one burst. Keeps the report
    // readable when a single attacker fires 200 identical requests.
    const seenBursts = new Set<string>();
    const evidenceLines: string[] = [];
    rawEvidence.forEach((line) => {
      const m = line.match(/^- (\S+) \|/);
      if (!m) { evidenceLines.push(line); return; }
      const time = m[1];
      // Truncate to minute (first 16 chars: "YYYY-MM-DDTHH:MM")
      const minute = time.substring(0, 16);
      const rest = line.substring(line.indexOf('|'));
      const key = `${minute}|${rest}`;
      if (seenBursts.has(key)) return;
      seenBursts.add(key);
      evidenceLines.push(line);
    });

    // Map aggregated reason text to an AbuseIPDB category.
    // We check BOTH the reason text AND the request paths, because the
    // backend gives one of: "WordPress admin access", ".env file access",
    // ".git directory access", "PHP file access", "SQL injection attempt",
    // "config.json access attempt", "XML-RPC access attempt", "403 Forbidden",
    // or "Suspicious pattern" — and most of these don't contain brute/ssh/etc.
    // keywords. The proper signal is the PATH, not the reason string.
    const reasonStr = Array.from(reasons).join(' ').toLowerCase();
    const pathStr = Array.from(paths).join(' ').toLowerCase();
    const combined = `${reasonStr} ${pathStr}`;
    let suggestedCategory = '';
    // Specific attack signatures first
    if (/union\s+select|sql\s+injection|sql\s+inject|\bor\s+1=1|--\s/i.test(combined)) suggestedCategory = 'SQL Injection';
    else if (/wp-admin|wp-login|wp-content|wp-includes|wordpress/i.test(combined)) suggestedCategory = 'Web App Attack';  // CMS recon
    else if (/xmlrpc/i.test(combined)) suggestedCategory = 'Web App Attack';  // XML-RPC attack vector
    else if (/\.env|\.git|config\.json|\.htaccess|\.htpasswd|\.ssh\/id_rsa/i.test(combined)) suggestedCategory = 'Web App Attack';  // Config file recon
    else if (/\.php/i.test(combined)) suggestedCategory = 'Web App Attack';  // PHP file recon
    else if (/brute|force|login|auth|password|\blogin\.php|\bauth\b/i.test(combined)) suggestedCategory = 'Brute-Force';
    else if (/ssh/i.test(combined)) suggestedCategory = 'SSH';
    else if (/ftp/i.test(combined)) suggestedCategory = 'FTP Brute-Force';
    else if (/scan|port|probe|nmap/i.test(combined)) suggestedCategory = 'Port Scan';
    else if (/xss|<script|javascript:|onerror=/i.test(combined)) suggestedCategory = 'Web App Attack';
    else if (/bot|spider|crawler|scrap/i.test(combined)) suggestedCategory = 'Bad Web Bot';
    else if (/ddos|flood|amplif/i.test(combined)) suggestedCategory = 'DDoS Attack';
    else if (/phish/i.test(combined)) suggestedCategory = 'Phishing';
    else if (/spam/i.test(combined)) suggestedCategory = 'Email Spam';
    else if (/proxy|vpn|tor/i.test(combined)) suggestedCategory = 'Open Proxy';
    else if (/spoof/i.test(combined)) suggestedCategory = 'Spoofing';
    // 403s on non-WP paths are usually recon scans
    else if (/403/.test(reasonStr) && paths.size > 0) suggestedCategory = 'Web App Attack';
    if (!suggestedCategory) suggestedCategory = 'Web App Attack';  // Safer default than "Hacking"

    const categoryNum = (CATEGORY_NUM as any)[suggestedCategory] || '22';

    // Build the report. Sections are clearly separated with blank lines
    // so AbuseIPDB renders it readably when pasted into their form.
    const distinctReasons = reasons.size;
    const distinctPaths = paths.size;
    const timeRange = firstTime === lastTime || !firstTime || !lastTime
      ? firstTime || 'unknown'
      : `${firstTime} → ${lastTime}`;

    let comment = `=== OPSEC INCIDENT REPORT ===\n`;
    comment += `Source IP: ${ip}\n`;
    comment += `Detection window: ${timeRange}\n`;
    comment += `Total suspicious requests: ${rows.length}\n`;
    comment += `Distinct request patterns: ${distinctReasons}\n`;
    comment += `Distinct target paths: ${distinctPaths}\n\n`;

    comment += `=== SUGGESTED ABUSEIPDB CATEGORY ===\n`;
    comment += `Category: ${suggestedCategory} (AbuseIPDB IDs: ${categoryNum})\n`;
    comment += `Please select these categories on the AbuseIPDB report form.\n\n`;

    comment += `=== PATTERNS DETECTED ===\n`;
    Array.from(reasons).forEach((r) => { comment += `• ${r}\n`; });
    comment += `\n`;

    // Contextual notes — explain WHY these patterns are malicious for THIS
    // specific site. The AbuseIPDB reviewer doesn't know your stack, so
    // spelling it out makes the report much more credible and actionable.
    const contextualNotes: string[] = [];
    const isWpSite = /wordpress|wp-admin|wp-content|wp-includes|wp-login/i.test(pathStr);
    if (isWpSite) {
      contextualNotes.push('• The target site is NOT a WordPress installation. Any request to a WordPress-specific path (wp-admin, wp-login.php, wp-content/, etc.) is unambiguously malicious reconnaissance — there is no legitimate reason for a real user to request these paths on this site.');
    }
    if (/\.env/i.test(pathStr)) {
      contextualNotes.push('• Requests to .env files are credential-theft reconnaissance. The .env file typically contains database passwords, API keys, and OAuth secrets.');
    }
    if (/\.git/i.test(pathStr)) {
      contextualNotes.push('• Requests to .git/ directories are source-code exfiltration attempts. A successful response would leak the full repository including hardcoded credentials.');
    }
    if (/config\.json|\.htaccess|\.htpasswd/i.test(pathStr)) {
      contextualNotes.push('• Requests to config/htaccess files are configuration-reconnaissance attempts to discover server settings, authentication requirements, or admin paths.');
    }
    if (/xmlrpc/i.test(pathStr)) {
      contextualNotes.push('• XML-RPC access is exploited for credential brute-force amplification and DDoS reflection (e.g., pingback abuse). Legitimate clients do not use XML-RPC on modern installations.');
    }
    if (/\.php/i.test(pathStr) && !/wp-|wordpress/i.test(pathStr)) {
      contextualNotes.push('• PHP file probing on a non-PHP site indicates automated vulnerability scanning. Real users do not probe random .php paths on a site that does not run PHP.');
    }
    if (/union\s+select|sql\s+injection|or\s+1=1|--/i.test(combined)) {
      contextualNotes.push('• SQL injection payload detected in request path. The attempt is unambiguously an attack — not a benign request.');
    }
    if (rows.length >= 50) {
      contextualNotes.push(`• High request volume (${rows.length} events in the detection window) indicates automated attack tooling rather than a human user.`);
    }

    comment += `=== EVIDENCE (${evidenceLines.length} deduped lines from ${rows.length} events) ===\n`;
    comment += evidenceLines.join('\n');
    comment += `\n\n`;

    if (contextualNotes.length > 0) {
      comment += `=== CONTEXT (why this is malicious for this site) ===\n`;
      comment += contextualNotes.join('\n');
      comment += `\n\n`;
    }

    comment += `=== NOTES ===\n`;
    comment += `• Evidence lines are deduplicated per minute (same status + same path within a 1-minute window is reported once).\n`;
    comment += `• Time stamps are server-render time, not log line time (so all rows from one snapshot will share the same timestamp).\n`;
    comment += `• This incident was automatically detected by iSentinel360 OPSEC monitoring (suspicious-pattern + 403-response filter on nginx access log).\n`;
    comment += `• The source IP has been pre-screened against our UFW whitelist; it is NOT on the allow list.\n`;

    // Truncate to 1024 chars (same cap as the EJS).
    if (comment.length > 1024) {
      comment = comment.substring(0, 1021) + '...';
    }

    return { comment, category: suggestedCategory, categoryNum };
  }

  // ---- Render ----
  return (
    <div className="min-h-screen bg-background text-foreground pb-12">
      <header className="px-4 py-3 bg-card border-b border-border flex items-center justify-between gap-3 sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/network')} className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          {/* Brand block — mirrors header.tsx: logo + "iSentinel360" + a
              small "OPSEC Monitoring" subtitle. The previous single-line
              title + IP/time/refresh/last-60m row did not fit on mobile
              (long string in a narrow column). Title is now the only
              thing competing for header space; the status line moved to
              a dedicated bar below (see <StatusBar> right after header). */}
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/logo.png"
              alt="iSentinel360"
              className="h-8 w-8 sm:h-9 sm:w-9 shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-sm sm:text-base leading-tight truncate">iSentinel360</span>
              <span className="text-[10px] sm:text-xs text-muted-foreground leading-tight truncate flex items-center gap-1">
                <Shield className="w-3 h-3 shrink-0" />
                {t('network.opsecTitle') || 'OPSEC Monitoring'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/*
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setActionMsg({ kind: 'ok', text: '🧪 Test toast — if you see this, the toast mechanism works' });
              setTimeout(() => setActionMsg(null), 4000);
            }}
            title="Test the toast notification"
            className="text-[10px] text-muted-foreground"
          >
            🧪
          </Button>
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="text-muted-foreground hover:text-foreground"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          {/* Hamburger menu — top right. Shows the 5 tabs as a list (so
              the user can jump directly without scrolling to the tab bar)
              plus a "Back to Network" link. Click outside / Escape to close. */}
          <div ref={menuRef} className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="text-muted-foreground hover:text-foreground"
            >
              <Menu className="w-4 h-4" />
            </Button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-2xl min-w-[220px] py-1 z-50"
                role="menu"
              >
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border mb-1">
                  Jump to section
                </div>
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted ${
                      activeTab === tab.key ? 'bg-muted/50 text-primary font-semibold' : 'text-foreground'
                    }`}
                    role="menuitem"
                  >
                    <span>{tab.icon}</span>
                    <span className="flex-1">{tab.label}</span>
                    {activeTab === tab.key && <span className="text-[10px]">●</span>}
                  </button>
                ))}
                <div className="border-t border-border my-1" />
                <button
                  onClick={() => { navigate('/network'); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-muted text-muted-foreground"
                  role="menuitem"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Back to Network</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* === Status bar === */}
      {/* Pulled OUT of the header on mobile because the combined
          "Your IP + clock + refresh + last-60m" line was being truncated
          in a 14px-tall column. As a full-width row it has the room it
          needs on both mobile and desktop. Hidden on print to avoid
          leaking the admin's IP in a PDF export. */}
      <div className="px-4 py-2 bg-muted/40 border-b border-border text-[11px] sm:text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 print:hidden">
        <span className="flex items-center gap-1">
          <span aria-hidden>📍</span>
          <span className="font-mono">Your IP: {myIp || '—'}</span>
        </span>
        {snapshot?.now && (
          <span className="flex items-center gap-1">
            <span className="opacity-50">·</span>
            <span>{fmtTime(snapshot.now)}</span>
          </span>
        )}
        {lastRefresh && (
          <span className="flex items-center gap-1">
            <span className="opacity-50">·</span>
            <span>refresh: {lastRefresh.toLocaleTimeString()}</span>
          </span>
        )}
        {autoBlockSummary && (autoBlockSummary.autoCount > 0 || autoBlockSummary.manualCount > 0 || autoBlockSummary.reconCount > 0) && (
          <span
            className="flex items-center gap-1"
            title={`Auto-blocked (≥95% confidence): ${autoBlockSummary.autoCount}\nManually blocked: ${autoBlockSummary.manualCount}\nRecon instant-blocked: ${autoBlockSummary.reconCount}`}
          >
            <span className="opacity-50">·</span>
            <span>🛡️ last 60m: {autoBlockSummary.autoCount} auto / {autoBlockSummary.manualCount} manual / {autoBlockSummary.reconCount} recon</span>
          </span>
        )}
      </div>

      {/* === Controls row === */}
      {/* The Auto-block toggle and Refresh button used to live in the
          header's right-side cluster. On mobile (320-414px wide) they
          got crushed against the logo/brand and overflowed or wrapped
          awkwardly. Moved to their own full-width row below the status
          bar so they always fit, on any screen size. */}
      <div className="px-4 py-2 bg-card border-b border-border flex flex-wrap items-center gap-3 text-xs">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoBlockEnabled}
            onChange={e => setAutoBlockEnabled(e.target.checked)}
            className="w-3.5 h-3.5"
          />
          <span className="text-foreground">Auto-block</span>
          <span className="text-[10px] text-muted-foreground hidden sm:inline">
            (≥95% confidence IPs blocked automatically)
          </span>
        </label>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh') || 'Refresh'}
        </Button>
      </div>

      {/* Toast — pinned just below the header so it doesn't get hidden behind it */}
      {actionMsg && (
        <div
          data-testid="opsec-toast"
          className={`fixed top-20 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-lg shadow-2xl text-base font-medium max-w-[90vw] flex items-center gap-2 ${
            actionMsg.kind === 'ok'
              ? 'bg-green-600 text-white border-2 border-green-400'
              : 'bg-destructive text-destructive-foreground border-2 border-red-400'
          }`}
        >
          {actionMsg.text}
          <button
            onClick={() => setActionMsg(null)}
            className="ml-2 text-white/80 hover:text-white text-sm"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <main className="p-4 space-y-6 max-w-6xl mx-auto">
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded text-sm">
            {error}
          </div>
        )}

        {/* === Overview tab: 6 summary cards in a 2-col grid === */}
        {activeTab === 'overview' && (() => {
          // Aggregate metrics for the summary cards. Computed from the
          // existing snapshot — no new API calls needed.
          // Field paths (per the /snapshot endpoint):
          //   snapshot.accessStats.topIPs            — [{ip, count, country, countryCode}]
          //   snapshot.accessStats.topPaths          — [{path, count}]
          //   snapshot.accessStats.suspiciousPatterns — [{ip, path, status, reason, ts}]
          //   snapshot.blockedIPs.ufw / .nginx        — array of strings
          //   snapshot.whitelistedIPs                 — [{ip, reason, ...}]
          const rawTopIpList = snapshot?.accessStats?.topIPs || [];
          // Filter out the admin's own IP and whitelisted IPs from the
          // "Top" calculation. Without this, a Cloudflare edge IP or the
          // admin's own traffic shows up as the "top attacker" — which is
          // misleading. The Suspicious table has toggleable filters for
          // these (hideMyIp / hideWhitelisted); the Overview card is
          // always-on (no toggles), so we apply the filter unconditionally.
          const filteredTopIpList = rawTopIpList.filter((e: any) =>
            e.ip !== (snapshot?.clientIP || '') && !whitelistedIpSet.has(e.ip)
          );
          const topIpList = filteredTopIpList;
          const topIp = topIpList[0];
          const totalAttacks = topIpList.reduce((sum: number, e: any) => sum + (e.count || 0), 0);
          // Top reason per IP — count reason occurrences from the
          // suspicious events and pick the most common one. If the IP
          // has no suspicious events (e.g. a heavy-but-benign bot),
          // it gets the fallback "mixed" / "high traffic" label.
          const reasonCountsByIp: Record<string, Record<string, number>> = {};
          for (const p of (snapshot?.accessStats?.suspiciousPatterns || [])) {
            if (!p.ip || !p.reason) continue;
            // Skip whitelisted / own IP from the reason map too — we
            // don't want a Cloudflare reason polluting the top reason
            // even if topIpList is already filtered.
            if (p.ip === (snapshot?.clientIP || '')) continue;
            if (whitelistedIpSet.has(p.ip)) continue;
            if (!reasonCountsByIp[p.ip]) reasonCountsByIp[p.ip] = {};
            reasonCountsByIp[p.ip][p.reason] = (reasonCountsByIp[p.ip][p.reason] || 0) + 1;
          }
          const topReasonByIp: Record<string, string> = {};
          for (const ip in reasonCountsByIp) {
            let max = 0;
            let topReason = '';
            for (const r in reasonCountsByIp[ip]) {
              if (reasonCountsByIp[ip][r] > max) {
                max = reasonCountsByIp[ip][r];
                topReason = r;
              }
            }
            topReasonByIp[ip] = topReason;
          }
          // Derive topCountries from topIPs (snapshot doesn't return it
          // separately, but each topIP has countryCode embedded)
          const topCountries = Object.values(
            topIpList.reduce((acc: Record<string, any>, ip: any) => {
              if (!ip.countryCode) return acc;
              if (!acc[ip.countryCode]) acc[ip.countryCode] = { country: ip.country, countryCode: ip.countryCode, count: 0 };
              acc[ip.countryCode].count += ip.count || 0;
              return acc;
            }, {})
          ).sort((a: any, b: any) => b.count - a.count) as Array<{ country: string; countryCode: string; count: number }>;
          const topCountry = topCountries[0];
          const ufwCount = (snapshot?.blockedIPs?.ufw || []).length;
          const nginxCount = (snapshot?.blockedIPs?.nginx || []).length;
          const wlCount = (snapshot?.whitelistedIPs || []).length;
          // Live threats: pulled from already-loaded state. These are
          // maintained by other useEffects so the cards stay current.
          const suspiciousCount = (snapshot?.accessStats?.suspiciousPatterns || []).length;
          // Pending count must match the Pending table's filter exactly.
          // The table hides IPs that are already denied, auto-blocked, or
          // removed (user dismissed them). If we just count keys, we'd
          // include stale localStorage entries the user already cleared.
          const pendingCount = Object.keys(pendingReviewIps || {}).filter(ip =>
            !denyIpSet.has(ip) &&
            !autoBlockedIps.has(ip) &&
            !removedReviewIps.has(ip)
          ).length;
          const velocityCount = (velocityData?.ips || []).filter((v: any) =>
            v.suggestedAction === 'AUTO_BLOCK' || v.suggestedAction === 'MANUAL'
          ).length;
          // Auto-blocked count: compute directly from the same data the
          // "Recently Auto-Blocked" table uses (nginxDenyLog). This
          // guarantees the card and the table show the same number, even
          // if the backend's /auto-block-summary endpoint has a quirk
          // (it once returned 0 for reconCount when the table had 4).
          // Counts lines with AUTO-BLOCKED OR RECON-INSTANT-BLOCKED in
          // the last 24h, excluding MANUAL-BLOCKED.
          const autoCutoff = Date.now() - 24 * 60 * 60 * 1000;
          let autoCount = 0;
          let reconCount = 0;
          for (const line of (nginxDenyLog || '').split('\n')) {
            if (!line.trim()) continue;
            if (line.includes('MANUAL-BLOCKED')) continue;
            const m = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/);
            if (!m) continue;
            const ts = new Date(m[1]).getTime();
            if (isNaN(ts) || ts < autoCutoff) continue;
            if (line.includes('RECON-INSTANT-BLOCKED')) {
              reconCount++;
            } else if (line.includes('AUTO-BLOCKED')) {
              autoCount++;
            }
          }
          const autoBlockedCount = autoCount + reconCount;
          // Last attack: most recent event in the snapshot
          // Use suspiciousPatterns (the per-row suspicious activity) as the
          // event source since it has ip + path + reason + ts.
          const allEvents = (snapshot?.accessStats?.suspiciousPatterns || []);
          const lastEvent = [...allEvents].sort((a: any, b: any) =>
            (b.ts || b.timestamp || b.lastSeen || '').localeCompare(a.ts || a.timestamp || a.lastSeen || '')
          )[0];
          return (
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Card 1: System Health */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Server className="w-4 h-4 text-primary" />
                  System
                </h3>
                <button onClick={() => setActiveTab('threats')} className="text-xs text-muted-foreground hover:text-foreground">
                  Details →
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <StatCard
                  label="Connections"
                  value={snapshot?.conntrackStats?.current ?? '—'}
                  sub={snapshot?.conntrackStats?.limit ? `/ ${snapshot.conntrackStats.limit}` : ''}
                  danger={!!(snapshot?.conntrackStats?.current && snapshot?.conntrackStats?.limit &&
                    (snapshot.conntrackStats.current / snapshot.conntrackStats.limit) > 0.8)}
                />
                <StatCard
                  label="Drop failures"
                  value={snapshot?.conntrackStats?.drop ?? '—'}
                  danger={(snapshot?.conntrackStats?.drop || 0) > 0}
                />
                <StatCard
                  label="Load avg"
                  value={snapshot?.systemStats?.loadAvg ? snapshot.systemStats.loadAvg.map((n: number) => n.toFixed(2)).join(' / ') : '—'}
                  sub="1 / 5 / 15 min"
                />
                <StatCard
                  label="Uptime"
                  value={fmtUptime(snapshot?.systemStats?.uptime)}
                />
              </div>
            </Card>

            {/* Card 2: Threats (last hour) — top 3 IPs with their top reason.
                Top reason = the most common `reason` field from this IP's
                suspicious events. Gives the admin instant context: not
                just "who" but "what they were doing". */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-orange-500" />
                  Threats (last hour)
                </h3>
                <button onClick={() => setActiveTab('threats')} className="text-xs text-muted-foreground hover:text-foreground">
                  Details →
                </button>
              </div>
              <div className="flex items-end gap-3 mb-3">
                <div>
                  <div className="text-3xl font-bold text-orange-500">{totalAttacks}</div>
                  <div className="text-xs text-muted-foreground">total events from {topIpList.length} IP{topIpList.length === 1 ? '' : 's'}</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top 3:</div>
                <table className="w-full text-xs table-fixed">
                  {/*
                    Column allocation. Every column has an EXPLICIT width so
                    the IP column doesn't flex and swallow all the leftover
                    space (which is what was pushing Country + Action against
                    each other on the right). The longest legal IPv4 is 15
                    chars; in our monospace font that's ~120px. Each
                    <th>/<td> below gets the matching padding so the
                    whitespace is consistent end-to-end.
                  */}
                  <colgroup>
                    <col className="w-4" />        {/* warning icon (16px) */}
                    <col className="w-[120px]" />  {/* IP — fixed, fits 15 chars */}
                    <col className="w-10" />       {/* Hits (40px, right-aligned) */}
                    <col className="w-[100px] hidden sm:table-column" />  {/* Reason, hidden on xs */}
                    <col className="w-8" />        {/* Country flag (32px) */}
                    <col className="w-[72px]" />   {/* Action badge (72px) */}
                  </colgroup>
                  <thead>
                    {/* Header row — same column widths as the data rows
                        below. Columns 1, 5, 6 are markers (icon, flag,
                        action badge) so they have no label; the three
                        meaningful columns get explicit headers. Bottom
                        border separates the header from the data so the
                        labels still read clearly when the body has 0-3
                        short rows. */}
                    <tr className="border-b border-border/60">
                      <th className="py-1 pr-1 text-center text-[10px] uppercase tracking-wider font-medium text-muted-foreground"></th>
                      <th className="py-1 pr-2 text-left text-[10px] uppercase tracking-wider font-medium text-muted-foreground">IP</th>
                      <th className="py-1 pr-2 text-right text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Hits</th>
                      <th className="py-1 pr-2 text-left text-[10px] uppercase tracking-wider font-medium text-muted-foreground hidden sm:table-cell">Reason</th>
                      <th className="py-1 pr-2 text-center text-[10px] uppercase tracking-wider font-medium text-muted-foreground" title="Country of origin (from GeoIP)">Country</th>
                      <th className="py-1 text-right text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topIpList.slice(0, 3).map((ip: any, i: number) => {
                      // Per-IP action — matches the same logic as the Last
                      // Attack card: Blocked if in UFW/nginx deny, WL if
                      // whitelisted, n/a otherwise.
                      const isBlocked = blockedIpSet.has(ip.ip) || denyIpSet.has(ip.ip);
                      const isWhitelisted = ip.ip && whitelistedIpSet.has(ip.ip);
                      const actionText = isBlocked ? 'Blocked' : isWhitelisted ? 'WL' : 'n/a';
                      const actionTone = isBlocked
                        ? 'text-red-500 font-semibold'
                        : isWhitelisted
                          ? 'text-green-600 dark:text-green-400 font-semibold'
                          : 'text-muted-foreground/60';
                      return (
                        <tr key={ip.ip} className="border-0">
                          {/* Negative-connotation marker — red ⚠ */}
                          <td className="py-1 pr-1 text-center text-red-500 align-middle" title={`Attacker #${i + 1}`}>⚠</td>
                          {/* IP — monospace, truncate with ellipsis. Fixed
                              width (120px) matches the colgroup so there's
                              no flex gap between the IP and the Hits column. */}
                          <td className="py-1 pr-2 font-mono truncate" title={ip.ip}>{ip.ip}</td>
                          {/* Count — right-aligned in a fixed 40px column */}
                          <td className="py-1 pr-2 text-right font-semibold text-orange-500 whitespace-nowrap align-middle">{ip.count}</td>
                          {/* Reason — hidden on xs. Width matches colgroup. */}
                          <td className="py-1 pr-2 text-muted-foreground text-[10px] truncate hidden sm:table-cell" title={topReasonByIp[ip.ip] || ''}>
                            {topReasonByIp[ip.ip] || 'mixed'}
                          </td>
                          {/* Country flag — placed next to the action so the
                              eye groups "where this IP is from" with "what we
                              did about it". Falls back to a small dash when
                              GeoIP hasn't resolved the country yet (e.g. cache
                              miss on a brand-new attacker). The 32px column
                              gives the flag emoji a little breathing room so
                              it doesn't visually crash into the Action badge. */}
                          <td className="py-1 pr-2 text-center align-middle text-base leading-none" title={ip.country || (ip.countryCode ? `Country code: ${ip.countryCode}` : 'Country unknown')}>
                            {ip.countryCode ? countryFlag(ip.countryCode) : <span className="text-muted-foreground/40 text-[10px]">—</span>}
                          </td>
                          {/* Action badge — right-aligned, fixed 72px so the
                              "Blocked" text never wraps and stays clear of
                              the flag column. */}
                          <td
                            className={`py-1 text-[10px] uppercase tracking-wider text-right whitespace-nowrap align-middle ${actionTone}`}
                            title={
                              isBlocked
                                ? 'IP is blocked in UFW and/or Nginx deny list'
                                : isWhitelisted
                                  ? 'IP is whitelisted — passes through UFW'
                                  : 'No action taken — IP is still free to attack'
                            }
                          >
                            {actionText}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {topIpList.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    No suspicious activity in the last hour
                  </div>
                )}
              </div>
            </Card>

            {/* Card 3: Top attacker countries (last 24h, top 3) */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Globe className="w-4 h-4 text-red-500" />
                  Top attacker countries
                  <span className="text-[10px] text-muted-foreground font-normal ml-1">
                    (last 24h)
                  </span>
                </h3>
                <button onClick={() => setActiveTab('historical')} className="text-xs text-muted-foreground hover:text-foreground">
                  Map →
                </button>
              </div>
              {topCountries24h.length > 0 ? (
                <div className="space-y-2">
                  {topCountries24h.slice(0, 3).map((c, i) => {
                    // Find the max count to compute a relative bar width
                    const maxCount = topCountries24h[0]?.count || 1;
                    const pct = Math.max(8, Math.round((c.count / maxCount) * 100));
                    return (
                      <div key={c.countryCode || c.country || i} className="flex items-center gap-2 text-sm">
                        <div className="text-2xl w-8 shrink-0">{countryFlag(c.countryCode || '')}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-medium truncate">{c.country || c.countryCode || 'Unknown'}</span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{c.count}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-0.5">
                            <div
                              className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No data yet</div>
              )}
            </Card>

            {/* Card 4: Block lists */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Ban className="w-4 h-4 text-destructive" />
                  Block lists
                </h3>
                <button onClick={() => setActiveTab('lists')} className="text-xs text-muted-foreground hover:text-foreground">
                  Manage →
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-bold text-destructive">{ufwCount}</div>
                  <div className="text-xs text-muted-foreground">UFW</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-destructive">{nginxCount}</div>
                  <div className="text-xs text-muted-foreground">Nginx</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{wlCount}</div>
                  <div className="text-xs text-muted-foreground">Whitelist</div>
                </div>
              </div>
            </Card>

            {/* Card 5: Live threats summary — each row has a tooltip so the
                numbers are self-explanatory. Pending and Auto (24h) were
                confusing without context; now hovering shows the meaning. */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Live threats
                </h3>
                <button onClick={() => setActiveTab('threats')} className="text-xs text-muted-foreground hover:text-foreground">
                  Details →
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between" title="IPs in the last 2000 nginx lines matching suspicious patterns (403, wp-*, .env, .git, etc.)">
                  <span className="text-muted-foreground">Suspicious</span>
                  <span className="font-semibold">{suspiciousCount}</span>
                </div>
                <div
                  className="flex justify-between cursor-help"
                  title="IPs in the 80-94% AbuseDB confidence range. NOT auto-blocked — these are waiting for your decision (Block / WL / Remove in the Pending table)."
                >
                  <span className="text-muted-foreground">Pending</span>
                  <span className="font-semibold">{pendingCount}</span>
                </div>
                <div
                  className="flex justify-between cursor-help"
                  title="IPs currently hammering the server (>60 req/min AND >5x their baseline). Auto-blocked when rate exceeds 200 req/min."
                >
                  <span className="text-muted-foreground">Velocity</span>
                  <span className="font-semibold text-orange-500">{velocityCount}</span>
                </div>
                <div
                  className="flex justify-between cursor-help"
                  title={`IPs the system silently auto-handled in the last 24h. Includes BOTH abuse-based auto-blocks (${autoCount} at 95-100% AbuseDB confidence) AND recon-based instant blocks (${reconCount} for known malicious paths like /wp-admin, .env, .git).`}
                >
                  <span className="text-muted-foreground">Auto (24h)</span>
                  <span className="font-semibold">{autoBlockedCount}</span>
                </div>
              </div>
            </Card>

            {/* Card 6: Last attack — four columns: the attack (IP + path +
                when), the reason it was flagged, the country of origin, and
                the action taken (Blocked / Whitelisted / n/a). Derived from
                the same suspiciousPatterns data as the other cards. The
                country flag mirrors the column placement used in the
                "Threats (last hour)" card so the eye groups "where + what
                we did" the same way in both cards. */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  Last attack
                </h3>
                <button onClick={() => setActiveTab('historical')} className="text-xs text-muted-foreground hover:text-foreground">
                  Investigate →
                </button>
              </div>
              {lastEvent ? (() => {
                const lastIp = lastEvent.ip || '';
                let lastAction = 'n/a';
                let lastActionTone = 'text-muted-foreground';
                if (lastIp) {
                  if (blockedIpSet.has(lastIp) || denyIpSet.has(lastIp)) {
                    lastAction = 'Blocked';
                    lastActionTone = 'text-red-500 font-semibold';
                  } else if (whitelistedIpSet.has(lastIp)) {
                    lastAction = 'Whitelisted';
                    lastActionTone = 'text-green-600 dark:text-green-400 font-semibold';
                  }
                }
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    {/* Column 1: The attack — IP + path + when */}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Attack</div>
                      <div className="font-mono truncate" title={lastIp}>{lastIp || '—'}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate" title={lastEvent.path || ''}>
                        {lastEvent.path || '—'}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {fmtTimeAgo(lastEvent.ts || lastEvent.timestamp || lastEvent.lastSeen)}
                      </div>
                    </div>
                    {/* Column 2: The reason */}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Reason</div>
                      <div className="truncate" title={lastEvent.reason || ''}>
                        {lastEvent.reason || lastEvent.status || 'suspicious'}
                      </div>
                    </div>
                    {/* Column 3: Country of origin (flag). Hidden on the
                        smallest screens via the 2-col mobile grid above;
                        sm: and up shows the full 4-col layout. Falls back
                        to a faint dash when GeoIP hasn't resolved the
                        country yet (e.g. brand-new attacker, cache miss). */}
                    <div className="min-w-0 hidden sm:block">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Country</div>
                      <div
                        className="text-base leading-none"
                        title={lastEvent.country || (lastEvent.countryCode ? `Country code: ${lastEvent.countryCode}` : 'Country unknown')}
                      >
                        {lastEvent.countryCode
                          ? countryFlag(lastEvent.countryCode)
                          : <span className="text-muted-foreground/40 text-[10px]">—</span>}
                      </div>
                    </div>
                    {/* Column 4: The action taken */}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Action</div>
                      <div className={lastActionTone} title={lastAction === 'n/a' ? 'No action taken — IP is still free to attack' : `IP is ${lastAction.toLowerCase()}`}>
                        {lastAction}
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <div className="text-sm text-muted-foreground">No recent attacks</div>
              )}
            </Card>
          </div>
        </section>
          );
        })()}

        {/* === Section 2: HTTP status + Top IPs + Top Paths === */}
        {activeTab === 'threats' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            {t('network.opsecHttpTitle') || 'HTTP status (last hour)'}
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <MiniStat label="200" value={snapshot?.accessStats?.status200 ?? 0} tone="ok" />
            <MiniStat label="400" value={snapshot?.accessStats?.status400 ?? 0} />
            <MiniStat label="403" value={snapshot?.accessStats?.status403 ?? 0} tone="warn" />
            <MiniStat label="404" value={snapshot?.accessStats?.status404 ?? 0} />
            <MiniStat label="499" value={snapshot?.accessStats?.status499 ?? 0} />
            <MiniStat label="5xx" value={(snapshot?.accessStats?.status500 ?? 0) + (snapshot?.accessStats?.status502 ?? 0)} tone="err" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <Card className="p-3">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                <Network className="w-3 h-3" /> {t('network.opsecTopIps') || 'Top IPs (last hour)'}
              </h3>
              <ol className="text-xs space-y-1 max-h-64 overflow-y-auto">
                {(snapshot?.accessStats?.topIPs || [])
                  .filter((row: any) => {
                    // User's own IP is controlled ONLY by hideMyIp,
                    // not by hideWhitelisted — otherwise whitelisting
                    // your own IP would make it un-restoreable.
                    if (row.ip === myIp) return !hideMyIp;
                    if (hideWhitelisted && whitelistedIpSet.has(row.ip)) return false;
                    return true;
                  })
                  .slice(0, 15)
                  .map((row: any) => (
                  <li key={row.ip} className="flex justify-between gap-2 font-mono">
                    <span className="truncate">{row.ip}</span>
                    <span className="text-muted-foreground">{row.count}</span>
                  </li>
                ))}
                {(!snapshot?.accessStats?.topIPs || snapshot.accessStats.topIPs.length === 0) && (
                  <li className="text-muted-foreground">—</li>
                )}
              </ol>
            </Card>
            <Card className="p-3">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                <ListOrdered className="w-3 h-3" /> {t('network.opsecTopPaths') || 'Top paths (last hour)'}
              </h3>
              <ol className="text-xs space-y-1 max-h-64 overflow-y-auto">
                {(snapshot?.accessStats?.topPaths || []).slice(0, 15).map((row: any, i: number) => (
                  <li key={i} className="flex justify-between gap-2 font-mono">
                    <span className="truncate">{row.path}</span>
                    <span className="text-muted-foreground">{row.count}</span>
                  </li>
                ))}
                {(!snapshot?.accessStats?.topPaths || snapshot.accessStats.topPaths.length === 0) && (
                  <li className="text-muted-foreground">—</li>
                )}
              </ol>
            </Card>
          </div>
        </section>
        )}

        {/* === Section 3: Suspicious activity === */}
        {activeTab === 'threats' && (
        <section>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="text-base font-bold sm:text-lg text-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {t('network.opsecSuspiciousTitle') || 'Suspicious activity'}
              <span className="text-xs text-muted-foreground font-normal">({filteredSuspicious.length})</span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value as any)}
                className="bg-background border border-input rounded px-2 py-1"
              >
                <option value="all">All</option>
                <option value="not_blocked">Not blocked</option>
                <option value="403">403 only</option>
                <option value="suspicious">Suspicious only</option>
              </select>
              <Input
                placeholder={t('network.opsecFilterIp') || 'Filter IP…'}
                value={filterIp}
                onChange={e => setFilterIp(e.target.value)}
                className="!py-1 !text-xs w-40"
              />
              <label className="flex items-center gap-1 text-muted-foreground">
                <input type="checkbox" checked={hideMyIp} onChange={e => setHideMyIp(e.target.checked)} />
                {t('network.opsecHideMyIp') || 'Hide my IP'}
              </label>
              <label className="flex items-center gap-1 text-muted-foreground">
                <input type="checkbox" checked={hideBlocked} onChange={e => setHideBlocked(e.target.checked)} />
                {t('network.opsecHideBlocked') || 'Hide blocked'}
              </label>
              <label className="flex items-center gap-1 text-muted-foreground">
                <input type="checkbox" checked={hideWhitelisted} onChange={e => setHideWhitelisted(e.target.checked)} />
                {t('network.opsecHideWhitelisted') || 'Hide whitelisted'}
              </label>
            </div>
          </div>

          {abuseEnabled && filteredSuspicious.length > 0 && (() => {
            // 80-95% range = user reviews before blocking.
            // When auto-block is paused (OFF), 95-100% IPs also surface here
            // so the admin can review them instead of being silently blocked.
            // 95-100% range is auto-blocked silently by the useEffect above (when ON).
            const reviewConf = filteredSuspicious.filter((row: any) => {
              const s = abuseScores[row.ip];
              if (!s) return false;
              const c = s.abuseConfidenceScore || 0;
              const inRange = autoBlockEnabled
                ? (c >= 80 && c < 95)
                : (c >= 80);
              return inRange && !denyIpSet.has(row.ip) && !autoBlockedIps.has(row.ip);
            });
            if (reviewConf.length === 0) return null;
            const rangeLabel = autoBlockEnabled ? '80-95%' : '80-100%';
            return (
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {reviewConf.length} IP(s) at {rangeLabel} AbuseIPDB confidence — review &amp; block
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={bulkBlockHighConfidence}
                  disabled={abuseBulkBusy}
                  className="border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                >
                  <Ban className="w-3 h-3 mr-1" />
                  {abuseBulkBusy ? 'Blocking…' : `Block ${reviewConf.length} IP(s)`}
                </Button>
              </div>
            );
          })()}

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left p-2 font-medium">{t('network.opsecColTime') || 'Time'}</th>
                    <th className="text-left p-2 font-medium">IP</th>
                    <th className="text-left p-2 font-medium" title="Country of origin (from GeoIP)">Country</th>
                    <th
                      className="text-right p-2 font-medium"
                      title="Total suspicious events from this IP across the current snapshot"
                    >
                      Hits
                    </th>
                    <th
                      className="text-right p-2 font-medium"
                      title="AbuseIPDB confidence-of-abuse (0-100). Red = high risk, green = low risk. Loads on-demand for the IPs in the table."
                    >
                      Score
                    </th>
                    <th className="text-left p-2 font-medium">Path</th>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-left p-2 font-medium">Reason</th>
                    <th className="text-right p-2 font-medium">{t('network.opsecColActions') || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuspicious.length === 0 && (
                    <tr>
                      <td colSpan={9} className="p-4 text-center text-muted-foreground text-xs">
                        <div className="space-y-1">
                          <div>{t('network.opsecNoSuspicious') || 'No suspicious activity in the last 2000 access-log lines.'}</div>
                          {suspicious.length > 0 ? (
                            <div className="text-[10px]">
                              ({suspicious.length} events hidden by filters — try "Not blocked" in the dropdown or uncheck "Hide blocked")
                            </div>
                          ) : (
                            <div className="text-[10px]">
                              (Last 2000 lines have no real recon probes — check the <span className="font-semibold">Historical</span> tab for a wider window)
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {filteredSuspicious.map((row: any, i: number) => {
                    const ip = row.ip;
                    const isUfw = blockedIpSet.has(ip);
                    const isNginx = denyIpSet.has(ip);
                    const isWhitelisted = whitelistedIpSet.has(ip);
                    return (
                      <tr key={i} className="border-t border-border hover:bg-muted/20">
                        <td className="p-2 font-mono whitespace-nowrap text-muted-foreground">{fmtTime(row.time)}</td>
                        <td className="p-2 font-mono">{ip}</td>
                        <td className="p-2 whitespace-nowrap" title={row.country || ''}>
                          {row.countryCode ? (
                            <span className="inline-flex items-center gap-1">
                              <span>{countryFlag(row.countryCode)}</span>
                              <span className="text-[10px] text-muted-foreground">{row.countryCode}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">
                          <span
                            className={
                              (hitsByIp[ip] || 0) >= 10
                                ? 'text-red-500 font-bold'
                                : (hitsByIp[ip] || 0) >= 3
                                  ? 'text-orange-500 font-semibold'
                                  : 'text-muted-foreground'
                            }
                            title={`${hitsByIp[ip] || 0} suspicious event(s) from this IP in the current snapshot`}
                          >
                            {hitsByIp[ip] || 0}
                          </span>
                        </td>
                        <td className="p-2 text-right font-mono whitespace-nowrap">
                          {(() => {
                            const s = abuseScores[ip];
                            if (s === undefined) return <span className="text-muted-foreground text-[10px]">…</span>;
                            if (s === null) return <span className="text-muted-foreground text-[10px]">n/a</span>;
                            const c = s.abuseConfidenceScore || 0;
                            const cls = c >= 80 ? 'text-red-600 dark:text-red-400 font-bold'
                                        : c >= 50 ? 'text-amber-600 dark:text-amber-400 font-semibold'
                                        : c >= 25 ? 'text-yellow-600 dark:text-yellow-400'
                                        : 'text-green-600 dark:text-green-400';
                            const r = s.totalReports || 0;
                            return (
                              <span
                                className={cls}
                                title={`AbuseIPDB: ${c}% confidence, ${r} report(s), ${s.isp || 'unknown ISP'}`}
                              >
                                {c}%
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-2 font-mono max-w-[16ch] truncate" title={row.path}>{row.path}</td>
                        <td className="p-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            String(row.status) === '403'
                              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                              : 'bg-red-500/15 text-red-700 dark:text-red-300'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="p-2 text-muted-foreground">{row.reason}</td>
                        <td className="p-2">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {isUfw ? (
                              <button
                                disabled={busy[`unblock-${ip}`]}
                                onClick={() => doAction(`unblock-${ip}`, () => api.opsecUnblockIp(ip))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Unblock in UFW"
                              >
                                UFW ✓
                              </button>
                            ) : (
                              <button
                                disabled={busy[`block-${ip}`]}
                                onClick={() => doAction(`block-${ip}`, () => api.opsecBlockIp(ip, `Blocked from OPSEC dashboard (UFW)`))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Block in UFW"
                              >
                                UFW
                              </button>
                            )}
                            {isNginx ? (
                              <button
                                disabled={busy[`unnginx-${ip}`]}
                                onClick={() => doAction(`unnginx-${ip}`, () => api.opsecRemoveDenyIp(ip))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Remove from Nginx deny"
                              >
                                NX ✓
                              </button>
                            ) : (
                              <button
                                disabled={busy[`nginx-${ip}`]}
                                onClick={() => doAction(`nginx-${ip}`, () => api.opsecDenyIp(ip, `Blocked from OPSEC dashboard (NGX)`))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Add to Nginx deny"
                              >
                                NX
                              </button>
                            )}
                            <a
                              href={`https://www.abuseipdb.com/check/${encodeURIComponent(ip)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background inline-flex items-center gap-1"
                              title={abuseScores[ip] && abuseScores[ip].abuseConfidenceScore != null
                                ? `AbuseIPDB: ${abuseScores[ip].abuseConfidenceScore}% confidence, ${abuseScores[ip].totalReports || 0} reports (${abuseScores[ip].isp || 'unknown ISP'})`
                                : 'Check IP on AbuseIPDB'}
                            >
                              <Eye className="w-3 h-3 inline" />
                              {abuseEnabled && (() => {
                                const s = abuseScores[ip];
                                if (s === undefined) return <span className="text-muted-foreground text-[9px]">…</span>;
                                if (s === null) return null; // fetch failed — don't show
                                const c = s.abuseConfidenceScore || 0;
                                const cls = c >= 80 ? 'text-red-600 dark:text-red-400 font-bold'
                                            : c >= 50 ? 'text-amber-600 dark:text-amber-400 font-semibold'
                                            : c >= 25 ? 'text-yellow-600 dark:text-yellow-400'
                                            : 'text-green-600 dark:text-green-400';
                                return <span className={cls}>{c}%</span>;
                              })()}
                            </a>
                            {isWhitelisted ? (
                              <span
                                className="px-1.5 py-0.5 text-[10px] text-green-700 dark:text-green-400 bg-green-500/15 border border-green-500/40 rounded font-medium"
                                title={(() => {
                                  // If this whitelisted IP is ALSO flagged on AbuseIPDB,
                                  // surface the irony as a tooltip so the admin knows
                                  // the auto-blocker correctly skipped it.
                                  const s = abuseScores[ip];
                                  const c = s ? (s.abuseConfidenceScore || 0) : 0;
                                  if (c >= 50) {
                                    return `Whitelisted (e.g. Cloudflare reverse proxy) AND flagged ${c}% on AbuseIPDB. Auto-blocker correctly skipped this — if this is actually malicious, remove from whitelist first.`;
                                  }
                                  return 'Whitelisted — auto-blocker will skip this IP even if AbuseIPDB flags it.';
                                })()}
                              >
                                WL ✓{(() => {
                                  const s = abuseScores[ip];
                                  const c = s ? (s.abuseConfidenceScore || 0) : 0;
                                  return c >= 50 ? ` ⚠${c}%` : '';
                                })()}
                              </span>
                            ) : (
                              <button
                                disabled={busy[`wl-${ip}`]}
                                onClick={() => doAction(`wl-${ip}`, () => api.opsecWhitelistIp(ip, 'Whitelisted from OPSEC dashboard'))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Whitelist"
                              >
                                WL
                              </button>
                            )}
                            <button
                              onClick={() => setReportRow(row)}
                              className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background"
                              title="Report (AbuseIPDB)"
                            >
                              RPT
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
        )}

        {/* === Section 3b: Pending for User Decision (80% - 94% IPs in AbuseDB) === */}
        {activeTab === 'threats' &&(() => {
          // PERSISTENT queue. Accumulates 80-94% IPs from every snapshot since
          // the user first loaded the page (or longer, thanks to localStorage).
          // 95-100% IPs are AUTO-BLOCKED — they NEVER appear here. This table
          // is for the gray-zone IPs that need a human decision.
          // Actions: Block (nginx deny), WL (whitelist), Remove (dismiss).
          const entries = Object.entries(pendingReviewIps)
            .filter(([ip]) =>
              !denyIpSet.has(ip) &&
              !autoBlockedIps.has(ip) &&
              !removedReviewIps.has(ip)
            )
            .map(([ip, entry]) => ({ ip, ...entry }));
          if (entries.length === 0) {
            return (
              <section>
                <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-amber-500" />
                  Pending for User Decision (80% - 94%) IPs in AbuseDB
                  <span className="text-xs text-muted-foreground font-normal">
                    (queue is empty — no 80-94% IPs in current or past snapshots)
                  </span>
                </h2>
                <Card className="p-4">
                  <p className="text-xs text-muted-foreground">
                    No 80-94% AbuseIPDB-flagged IPs have appeared yet. IPs at 95%+ are auto-blocked silently
                    and shown in the &ldquo;Recently Auto-Blocked&rdquo; table below.
                  </p>
                </Card>
              </section>
            );
          }
          entries.sort((a, b) => b.lastSeen - a.lastSeen);
          return (
            <section>
              <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
                <ListOrdered className="w-4 h-4 text-amber-500" />
                Pending for User Decision (80% - 94%) IPs in AbuseDB
                <span className="text-xs text-muted-foreground font-normal">
                  ({entries.length} IP{entries.length === 1 ? '' : 's'} — block, whitelist, or remove)
                </span>
                {entries.length > 0 && (
                  <button
                    onClick={() => {
                      if (confirm(`Clear all ${entries.length} pending IP${entries.length === 1 ? '' : 's'} from the queue?`)) {
                        setPendingReviewIps({});
                      }
                    }}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline"
                    title="Empty the entire pending queue"
                  >
                    Clear queue
                  </button>
                )}
              </h2>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="text-left p-2 font-medium">IP</th>
                        <th className="text-left p-2 font-medium">Last seen</th>
                        <th className="text-left p-2 font-medium">First seen</th>
                        <th className="text-left p-2 font-medium">Last path</th>
                        <th className="text-left p-2 font-medium">Last reason</th>
                        <th className="text-right p-2 font-medium">Score</th>
                        <th className="text-right p-2 font-medium">Hits</th>
                        <th className="text-right p-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => {
                        const ip = entry.ip;
                        const conf = entry.confidence;
                        const cls = conf >= 80 ? 'text-red-600 dark:text-red-400 font-bold'
                                    : conf >= 50 ? 'text-amber-600 dark:text-amber-400 font-semibold'
                                    : 'text-green-600 dark:text-green-400';
                        return (
                          <tr key={`pending-${ip}`} className="border-t border-border">
                            <td className="p-2 font-mono">{ip}</td>
                            <td className="p-2 text-muted-foreground font-mono whitespace-nowrap">{new Date(entry.lastSeen).toLocaleString()}</td>
                            <td className="p-2 text-muted-foreground font-mono whitespace-nowrap text-[10px]">{new Date(entry.firstSeen).toLocaleDateString()}</td>
                            <td className="p-2 font-mono max-w-[20ch] truncate" title={entry.path}>{entry.path || '—'}</td>
                            <td className="p-2 text-muted-foreground">{entry.reason || '—'}</td>
                            <td className="p-2 text-right"><span className={cls}>{conf}%</span></td>
                            <td className="p-2 text-right text-muted-foreground">{entry.count}</td>
                            <td className="p-2 text-right">
                              <div className="flex items-center justify-end gap-1 flex-wrap">
                                <button
                                  disabled={busy[`nginx-${ip}`]}
                                  onClick={() => {
                                    doAction(`nginx-${ip}`, () => api.opsecDenyIp(ip, `Blocked from Pending Decision (${conf}%)`));
                                    setPendingReviewIps((prev) => {
                                      const next = { ...prev };
                                      delete next[ip];
                                      return next;
                                    });
                                  }}
                                  className="px-1.5 py-0.5 border border-destructive/50 text-destructive rounded text-[10px] hover:bg-destructive/10 disabled:opacity-50"
                                  title="Block in Nginx (deny)"
                                >
                                  Block
                                </button>
                                <button
                                  disabled={busy[`wl-${ip}`]}
                                  onClick={() => {
                                    doAction(`wl-${ip}`, () => api.opsecWhitelistIp(ip, 'Whitelisted from Pending Decision'));
                                    setPendingReviewIps((prev) => {
                                      const next = { ...prev };
                                      delete next[ip];
                                      return next;
                                    });
                                  }}
                                  className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                  title="Whitelist (known good — adds to UFW allow)"
                                >
                                  WL
                                </button>
                                <button
                                  onClick={() => setRemovedReviewIps((prev) => {
                                    const next = new Set(prev);
                                    next.add(ip);
                                    return next;
                                  })}
                                  className="px-1.5 py-0.5 border border-orange-500/50 text-orange-600 dark:text-orange-400 rounded text-[10px] hover:bg-orange-500/10"
                                  title="Remove permanently — won't appear again, even after reload"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>
          );
        })()}

        {/* === Section 3c-velocity: Rate-limit velocity (sudden request spikes) === */}
        {/* Independent of AbuseIPDB — flags IPs that suddenly start hammering
            the server, even if their global reputation is clean. Auto-blocks
            at 200+ req/min AND 5x baseline (and not whitelisted). Always
            visible so the user can confirm the feature is wired up. */}
        {activeTab === 'threats' &&(() => {
          const ips = velocityData?.ips || [];
          // Only show IPs that need action (suggested AUTO_BLOCK or MANUAL).
          // WATCH = whitelisted / already-blocked / own IP — skip.
          const actionable = ips.filter((v: any) => v.suggestedAction === 'AUTO_BLOCK' || v.suggestedAction === 'MANUAL');
          return (
            <section className="mb-6">
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span className="text-orange-500">⚡</span>
                    {t('network.opsecVelocityTitle') || 'Rate-limit velocity'}
                    <span className="text-xs text-muted-foreground ml-2">
                      {velocityData
                        ? (actionable.length === 0
                            ? '(no active spikes)'
                            : `(${actionable.length} spike${actionable.length === 1 ? '' : 's'} in last ${velocityData.currentWindowMinutes} min, ${velocityData.minCurrent}+ req/min, ${velocityData.minRatio}x+ baseline)`)
                        : '(loading...)'}
                    </span>
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchVelocity}
                      disabled={velocityLoading}
                      className="text-xs px-2 py-1 border border-input rounded hover:bg-background disabled:opacity-50"
                    >
                      {velocityLoading ? '…' : '↻'}
                    </button>
                  </div>
                </div>
                {velocityError && <div className="text-xs text-red-500 mb-2">{velocityError}</div>}
                <p className="text-xs text-muted-foreground mb-3">
                  IPs whose current request rate is at least 5x their baseline
                  (computed over the last {velocityData?.windowMinutes || 60} min, excluding the last
                  {' '}{velocityData?.recentExcludedMinutes || 5} min). Auto-blocked at
                  {' '}{velocityData?.autoMinCurrent || 200}+ req/min. Whitelisted IPs are never blocked.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="text-left py-1 px-2">IP</th>
                        <th className="text-left py-1 px-2">Country</th>
                        <th className="text-right py-1 px-2">Current</th>
                        <th className="text-right py-1 px-2">Baseline</th>
                        <th className="text-right py-1 px-2">Ratio</th>
                        <th className="text-left py-1 px-2">Sample path</th>
                        <th className="text-left py-1 px-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionable.map((v: any) => {
                        const isAuto = v.suggestedAction === 'AUTO_BLOCK';
                        return (
                          <tr key={v.ip} className="border-b border-border/40 hover:bg-muted/30">
                            <td className="py-1.5 px-2 font-mono">{v.ip}</td>
                            <td className="py-1.5 px-2">
                              {v.countryCode ? `${countryFlag(v.countryCode)} ${v.countryCode}` : '—'}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-red-500 font-semibold">
                              {v.currentRate} <span className="text-[10px] text-muted-foreground">/min</span>
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                              {v.baselineRate}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono">
                              <span className={v.ratio >= 10 ? 'text-red-500 font-bold' : 'text-orange-500'}>
                                {v.ratio}x
                              </span>
                            </td>
                            <td className="py-1.5 px-2 font-mono text-[10px] text-muted-foreground max-w-[200px] truncate" title={v.samplePath || ''}>
                              {v.samplePath || '—'}
                            </td>
                            <td className="py-1.5 px-2">
                              {isAuto ? (
                                <button
                                  onClick={async () => {
                                    try {
                                      const r = await api.opsecVelocityBlock(
                                        [{ ip: v.ip, currentRate: v.currentRate, ratio: v.ratio }],
                                        true,
                                        `Rate-limit velocity spike (${v.currentRate} req/min, ${v.ratio}x baseline)`
                                      );
                                      flash('ok', `Auto-blocked ${v.ip}`);
                                      fetchVelocity();
                                    } catch (e: any) {
                                      flash('err', e?.message || 'Block failed');
                                    }
                                  }}
                                  className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-semibold hover:bg-red-700"
                                  title="Block in UFW + nginx now"
                                >
                                  AUTO-BLOCK
                                </button>
                              ) : (
                                <div className="flex gap-1">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const r = await api.opsecVelocityBlock(
                                          [{ ip: v.ip, currentRate: v.currentRate, ratio: v.ratio }],
                                          false,
                                          `Rate-limit velocity spike (${v.currentRate} req/min, ${v.ratio}x baseline)`
                                        );
                                        flash('ok', `Blocked ${v.ip}`);
                                        fetchVelocity();
                                      } catch (e: any) {
                                        flash('err', e?.message || 'Block failed');
                                      }
                                    }}
                                    className="px-2 py-0.5 bg-destructive text-destructive-foreground rounded text-[10px] font-semibold hover:bg-destructive/90"
                                  >
                                    Block
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.opsecWhitelistIp(v.ip, 'Rate-limit velocity — false positive, allow');
                                        flash('ok', `Whitelisted ${v.ip}`);
                                        fetchVelocity();
                                      } catch (e: any) {
                                        flash('err', e?.message || 'WL failed');
                                      }
                                    }}
                                    className="px-2 py-0.5 border border-input rounded text-[10px] hover:bg-background"
                                    title="Whitelist (known good — adds to UFW allow)"
                                  >
                                    WL
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {actionable.length === 0 && !velocityLoading && (
                  <div className="text-xs text-muted-foreground text-center py-3">
                    No active velocity spikes.
                  </div>
                )}
              </Card>
            </section>
          );
        })()}

        {/* === Section 3c: Recently Auto-Blocked (last 24h, from deny log) === */}
        {/* Shows IPs the auto-blocker handled while the user was away.
            For users not 24/7 at the screen: come back, see what was done,
            optionally unblock false positives or whitelist.
            Includes BOTH AUTO-BLOCKED (95-100% abuse confidence) AND
            RECON-INSTANT-BLOCKED (recon paths — WP/.env/.git/xmlrpc/.php)
            since both go to nginx deny via the same log file. */}
        {activeTab === 'threats' &&(() => {
          if (!nginxDenyLog) return null;
          const lines = nginxDenyLog.split('\n').filter(Boolean);
          const cutoff = Date.now() - 24 * 60 * 60 * 1000;
          const entries: { ts: number; ip: string; confidence: number; reason: string; source: 'AUTO' | 'RECON'; line: string }[] = [];
          for (const line of lines) {
            // Skip manual blocks (those have user intent, not auto-handler)
            if (line.includes('MANUAL-BLOCKED')) continue;
            // We want AUTO-BLOCKED (95-100% abuse confidence) AND RECON-INSTANT-BLOCKED
            if (!line.includes('AUTO-BLOCKED') && !line.includes('RECON-INSTANT-BLOCKED')) continue;
            const tsMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/);
            const ipMatch = line.match(/NGINX DENY ADDED: (\S+)/);
            if (!tsMatch || !ipMatch) continue;
            const ts = new Date(tsMatch[1]).getTime();
            if (isNaN(ts) || ts < cutoff) continue;
            const isRecon = line.includes('RECON-INSTANT-BLOCKED');
            // For AUTO-BLOCKED lines: "AUTO-BLOCKED: 95% abuse confidence"
            // For RECON-INSTANT-BLOCKED lines: "RECON-INSTANT-BLOCKED: <reason> [(UFW+NX)]"
            let confidence = 0;
            let reason = '';
            if (isRecon) {
              // Extract reason: "RECON-INSTANT-BLOCKED: <reason>" until " (" or end
              const reasonMatch = line.match(/RECON-INSTANT-BLOCKED:\s*([^()]+?)(?:\s*\(.*\))?$/);
              reason = reasonMatch ? reasonMatch[1].trim() : 'Recon path probe';
              // Confidence: 0% for recon (we don't check AbuseIPDB for these)
              // but the user said "who cares if it's 0%, recon is justified"
            } else {
              const confMatch = line.match(/AUTO-BLOCKED:\s*(\d+)%/);
              confidence = confMatch ? parseInt(confMatch[1], 10) : 0;
            }
            entries.push({
              ts,
              ip: ipMatch[1],
              confidence,
              reason,
              source: isRecon ? 'RECON' : 'AUTO',
              line,
            });
          }
          entries.sort((a, b) => b.ts - a.ts); // newest first
          // Hits per IP: how many times this IP shows up across all
          // recent blocks. An IP that was blocked 5 times in 24h is
          // a worse offender than one blocked once — useful for the
          // admin to prioritize unblock / whitelist decisions.
          const blockedHitsByIp: Record<string, number> = {};
          for (const e of entries) blockedHitsByIp[e.ip] = (blockedHitsByIp[e.ip] || 0) + 1;
          if (entries.length === 0) return null;
          return (
            <section>
              <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-500" />
                Recently Auto-Blocked
                <span className="text-xs text-muted-foreground font-normal">
                  ({entries.length} IP{entries.length === 1 ? '' : 's'} in the last 24h — review what was blocked while you were away)
                </span>
              </h2>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-card text-muted-foreground sticky top-0 z-10 shadow-sm">
                      <tr>
                        <th className="text-left p-2 font-medium">Time</th>
                        <th className="text-left p-2 font-medium">IP</th>
                        <th
                          className="text-right p-2 font-medium"
                          title="How many times this IP was blocked in the last 24h"
                        >
                          Hits
                        </th>
                        <th className="text-left p-2 font-medium">Source</th>
                        <th className="text-left p-2 font-medium">Reason</th>
                        <th className="text-right p-2 font-medium">Conf</th>
                        <th className="text-right p-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => (
                        <tr key={`${e.ip}-${e.ts}-${i}`} className="border-t border-border">
                          <td className="p-2 text-muted-foreground font-mono whitespace-nowrap">
                            {new Date(e.ts).toLocaleString()}
                          </td>
                          <td className="p-2 font-mono">{e.ip}</td>
                          <td className="p-2 text-right font-mono">
                            <span
                              className={
                                (blockedHitsByIp[e.ip] || 0) >= 3
                                  ? 'text-red-500 font-bold'
                                  : (blockedHitsByIp[e.ip] || 0) >= 2
                                    ? 'text-orange-500 font-semibold'
                                    : 'text-muted-foreground'
                              }
                              title={`${blockedHitsByIp[e.ip] || 0} block event(s) for this IP in the last 24h`}
                            >
                              {blockedHitsByIp[e.ip] || 0}
                            </span>
                          </td>
                          <td className="p-2">
                            {e.source === 'RECON' ? (
                              <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-700 dark:text-orange-300 rounded text-[10px] font-medium">
                                RECON
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-700 dark:text-red-300 rounded text-[10px] font-medium">
                                AUTO
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-muted-foreground max-w-[30ch] truncate" title={e.reason || (e.source === 'AUTO' ? `≥95% abuse confidence` : 'Recon path probe')}>
                            {e.source === 'RECON'
                              ? (e.reason || 'Recon path probe')
                              : `≥${e.confidence}% abuse`}
                          </td>
                          <td className="p-2 text-right">
                            {e.source === 'RECON' ? (
                              // 0% for recon — user said "who cares if it's 0%, recon is justified"
                              <span className="text-orange-600 dark:text-orange-400 font-mono text-[10px]">n/a</span>
                            ) : (
                              <span className={e.confidence >= 95 ? 'text-red-600 dark:text-red-400 font-bold' : 'text-amber-600 dark:text-amber-400'}>
                                {e.confidence}%
                              </span>
                            )}
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              <button
                                disabled={busy[`unnginx-${e.ip}`]}
                                onClick={() => doAction(`unnginx-${e.ip}`, () => api.opsecRemoveDenyIp(e.ip))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Remove from Nginx deny (false positive?)"
                              >
                                Unblock
                              </button>
                              <button
                                disabled={busy[`wl-${e.ip}`]}
                                onClick={() => doAction(`wl-${e.ip}`, () => api.opsecWhitelistIp(e.ip, 'Whitelisted from Recently Auto-Blocked'))}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                title="Whitelist (known good)"
                              >
                                WL
                              </button>
                              <a
                                href={`https://www.abuseipdb.com/check/${encodeURIComponent(e.ip)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background inline-flex items-center gap-1"
                                title="Check on AbuseIPDB"
                              >
                                <Eye className="w-3 h-3 inline" />
                              </a>
                              <button
                                onClick={() => {
                                  // Open the report modal with a synthetic row for this IP
                                  // so buildReportComment can produce a good verbose report.
                                  // The path is unknown from the deny log alone, but the
                                  // reason tells us what kind of recon it was.
                                  const syntheticPath = e.source === 'RECON'
                                    ? guessReconPathFromReason(e.reason)
                                    : '';
                                  setReportRow({
                                    ip: e.ip,
                                    path: syntheticPath,
                                    reason: e.source === 'RECON' ? e.reason : 'High abuse confidence',
                                    time: new Date(e.ts).toISOString(),
                                    status: e.source === 'RECON' ? 'suspicious' : '403',
                                  });
                                }}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background"
                                title="Report (AbuseIPDB) — especially useful for 0% recon blocks"
                              >
                                RPT
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>
          );
        })()}

        {/* === Section 3d: Historical Attacks (date-range query over access log) === */}
        {/* Goes BEYOND the last hour's snapshot. Reads the nginx access log
            over a user-specified date range, filters by IP/path, returns
            suspicious events with aggregations (top IPs, top paths,
            status counts, hourly timeline). Use this for:
              - Investigating an attack that happened 2 days ago
              - Spotting patterns (an IP that comes back every Monday)
              - Building a per-IP drill-down
              - Exporting CSV for incident reports
        */}
        {activeTab === 'historical' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Historical Attacks (date-range query)
            <span className="text-xs text-muted-foreground font-normal">
              (reads the last 50,000 lines of the nginx access log)
            </span>
          </h2>
          <Card className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end">
              <div>
                <Label>Start</Label>
                <Input
                  type="datetime-local"
                  value={histStart}
                  onChange={e => setHistStart(e.target.value)}
                />
              </div>
              <div>
                <Label>End</Label>
                <Input
                  type="datetime-local"
                  value={histEnd}
                  onChange={e => setHistEnd(e.target.value)}
                />
              </div>
              <div>
                <Label>IP (optional)</Label>
                <Input
                  value={histIp}
                  onChange={e => setHistIp(e.target.value)}
                  placeholder="1.2.3.4"
                />
              </div>
              <div>
                <Label>Path (optional)</Label>
                <Input
                  value={histPath}
                  onChange={e => setHistPath(e.target.value)}
                  placeholder="wp-admin"
                />
              </div>
              <Button
                disabled={histLoading}
                onClick={() => runHistoricalQuery()}
              >
                {histLoading ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Running…</> : 'Run query'}
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
              {[
                { label: 'Last 12h', ms: 12 * 60 * 60 * 1000,   maxLines: 50_000 },
                { label: 'Last 7d',  ms: 7 * 24 * 60 * 60 * 1000, maxLines: 500_000 },
                { label: 'Last 30d', ms: 30 * 24 * 60 * 60 * 1000, maxLines: 1_000_000 },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => {
                    // Set state first, then run. The query uses the
                    // freshly-set histStart/histEnd via the helper.
                    const end = new Date();
                    const start = new Date(end.getTime() - r.ms);
                    setHistEnd(end.toISOString().substring(0, 16));
                    setHistStart(start.toISOString().substring(0, 16));
                    // Auto-trigger the query so the data updates
                    // immediately — no need to also click "Run query".
                    runHistoricalQuery({
                      start: start.toISOString(),
                      end: end.toISOString(),
                      maxLines: r.maxLines,
                    });
                  }}
                  disabled={histLoading}
                  className="px-2 py-0.5 border border-input rounded hover:bg-background disabled:opacity-50"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </Card>

          {histError && (
            <div className="mt-3 p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded text-xs">
              {histError}
            </div>
          )}

          {histData && (
            <>
              {/* "Last updated" indicator — confirms to the user that the
                  data is current (or shows how stale it is if polling
                  failed). The historical tab is the only one with a
                  distinct refresh marker because the user manually
                  queries date ranges there; the rest of the dashboard
                  is snapshot-polled. */}
              <div className="mt-3 text-[10px] text-muted-foreground flex items-center gap-1">
                <span aria-hidden>🕐</span>
                <span>
                  Last updated:{' '}
                  {histLastRefresh
                    ? histLastRefresh.toLocaleTimeString()
                    : histData.range?.end
                      ? new Date(histData.range.end).toLocaleTimeString() + ' (cached)'
                      : '—'}
                </span>
                {histLoading && <RefreshCw className="w-3 h-3 animate-spin ml-1" />}
              </div>
              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <StatCard label="Total events" value={histData.total} />
                <StatCard label="Distinct IPs" value={histData.topIPs.length} />
                <StatCard label="Top status" value={(() => {
                  // Object.entries({}) yields `[string, unknown][]` in
                  // strict mode, so `b[1] - a[1]` errors as "Object is of
                  // type 'unknown'". Cast to a number-keyed tuple for
                  // the arithmetic and the .sort comparator.
                  const entries = Object.entries(histData.statusCounts || {}) as [string, number][];
                  if (entries.length === 0) return '—';
                  entries.sort((a, b) => b[1] - a[1]);
                  return entries[0][0];
                })()} sub={(() => {
                  const entries = Object.entries(histData.statusCounts || {}) as [string, number][];
                  if (entries.length === 0) return '';
                  entries.sort((a, b) => b[1] - a[1]);
                  return `${entries[0][1]} events`;
                })()} />
                <StatCard label="Hourly peak" value={(() => {
                  const hourly = histData.hourly || [];
                  if (hourly.length === 0) return '—';
                  return Math.max(...hourly.map((h: any) => h.count));
                })()} sub={(() => {
                  const hourly = histData.hourly || [];
                  if (hourly.length === 0) return '';
                  const peak = hourly.reduce((max: any, h: any) => h.count > max.count ? h : max, hourly[0]);
                  return peak.hour.replace('T', ' ') + ':00';
                })()} />
              </div>

              {/* Top IPs and Top Paths side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Card className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Top attacker IPs</h3>
                    <select
                      value={histCountryFilter}
                      onChange={e => setHistCountryFilter(e.target.value)}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-input bg-background text-foreground"
                      title="Filter by country"
                    >
                      <option value="all">All countries</option>
                      {(() => {
                        const seen = new Set<string>();
                        const opts: string[] = [];
                        histData.topIPs.forEach((r: any) => {
                          if (r.countryCode && !seen.has(r.countryCode)) {
                            seen.add(r.countryCode);
                            opts.push(r.countryCode);
                          }
                        });
                        return opts.sort().map(cc => (
                          <option key={cc} value={cc}>{countryFlag(cc)} {cc} ({histData.topIPs.filter((r: any) => r.countryCode === cc).length})</option>
                        ));
                      })()}
                    </select>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-card text-muted-foreground sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">IP</th>
                          <th className="text-left p-2 font-medium">Country</th>
                          <th className="text-left p-2 font-medium">City/ISP</th>
                          <th className="text-right p-2 font-medium">Hits</th>
                          <th className="text-right p-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {histData.topIPs
                          .filter((row: any) => histCountryFilter === 'all' || row.countryCode === histCountryFilter)
                          .map((row: any) => (
                          <tr key={row.ip} className="border-t border-border">
                            <td className="p-2 font-mono">{row.ip}</td>
                            <td className="p-2">
                              {row.countryCode ? (
                                <span title={row.country}>
                                  <span className="mr-1">{countryFlag(row.countryCode)}</span>
                                  <span className="text-muted-foreground">{row.countryCode}</span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-2 text-muted-foreground text-[10px] max-w-[16ch] truncate" title={`${row.city || ''} ${row.isp || ''}`}>
                              {row.city || row.isp || '—'}
                            </td>
                            <td className="p-2 text-right">{row.count}</td>
                            <td className="p-2 text-right">
                              <button
                                onClick={async () => {
                                  setHistDrillIp(row.ip);
                                  setHistDrillLoading(true);
                                  setHistDrillData(null);
                                  try {
                                    const data = await api.opsecHistoricalAttacks({
                                      start: histData.range.start,
                                      end: histData.range.end,
                                      ip: row.ip,
                                      limit: 10000,
                                    });
                                    setHistDrillData(data.events);
                                  } catch (e: any) {
                                    setHistError('Drill query failed: ' + (e?.message || 'unknown'));
                                  } finally {
                                    setHistDrillLoading(false);
                                  }
                                }}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background"
                                title="Drill down — see all events for this IP in the date range"
                              >
                                Drill
                              </button>
                            </td>
                          </tr>
                        ))}
                        {histData.topIPs.filter((row: any) => histCountryFilter === 'all' || row.countryCode === histCountryFilter).length === 0 && (
                          <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">—</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-3">
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Top targeted paths</h3>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-card text-muted-foreground sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Path</th>
                          <th className="text-right p-2 font-medium">Hits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {histData.topPaths.map((row: any) => (
                          <tr key={row.path} className="border-t border-border">
                            <td className="p-2 font-mono max-w-[40ch] truncate" title={row.path}>{row.path || '—'}</td>
                            <td className="p-2 text-right">{row.count}</td>
                          </tr>
                        ))}
                        {histData.topPaths.length === 0 && (
                          <tr><td colSpan={2} className="p-3 text-center text-muted-foreground">—</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              {/* Hourly timeline (text-bar chart) — all times shown in LOCAL
                  time (the browser's timezone). Fills in all hours between
                  start and end so the user can see the data is current.
                  Backend stores in UTC (canonical); frontend converts at
                  display time. The iteration is in UTC because that's
                  what the data uses internally — the rendered label is
                  the local equivalent. */}
              {histData.hourly && histData.hourly.length > 0 && (() => {
                // Build a lookup of UTC hourKey → count from the response
                const byHour = new Map<string, number>();
                for (const h of histData.hourly) byHour.set(h.hour, h.count);
                // Convert one UTC hourKey ("2026-08-04T21") to a local
                // display label ("2026-08-05 00" for Moscow, since UTC 21
                // is local 00 the next day). Uses the browser's tz.
                const utcKeyToLocalLabel = (utcKey: string): string => {
                  const d = new Date(utcKey + ':00:00.000Z');
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  const hh = String(d.getHours()).padStart(2, '0');
                  return `${yyyy}-${mm}-${dd} ${hh}`;
                };
                // Walk every hour from start to end (UTC internally).
                // Each row is a UTC hourKey, displayed as local.
                // Defensive range: backend always returns range, but if
                // it's missing we fall back to the first/last hour with
                // data, or to "last 24h" if the hourly array is empty.
                const rangeStartRaw = histData.range?.start ? new Date(histData.range.start) : null;
                const endRaw = histData.range?.end ? new Date(histData.range.end) : null;
                let rangeStart = rangeStartRaw;
                let end = endRaw;
                if (!rangeStart && histData.hourly && histData.hourly.length > 0) {
                  // Derive from hourly: first hour in the response.
                  const firstHour = histData.hourly[0].hour;
                  rangeStart = new Date(firstHour + ':00:00.000Z');
                }
                if (!end) {
                  // Default to "now" if backend didn't send it.
                  end = new Date();
                }
                if (!rangeStart) {
                  // No data at all — synthesize a 24h window ending at end.
                  rangeStart = new Date(end.getTime() - 24 * 60 * 60 * 1000);
                }
                // The timeline TABLE is capped to the last 24h. The map and
                // the rest of the dashboard keep the full range from
                // histData.range, so the user can still drill into older
                // data via the world map and the top-IPs table. The table
                // would get long and awkward at 7d, so we clamp the left
                // edge to end - 24h here only.
                const start = rangeStart && end
                  ? new Date(Math.max(rangeStart.getTime(), end.getTime() - 24 * 60 * 60 * 1000))
                  : rangeStart;
                let rows: { label: string; count: number; isFuture: boolean; wasTrimmed: boolean }[];
                if (start && end) {
                  rows = [];
                  const cur = new Date(Date.UTC(
                    start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(),
                    start.getUTCHours(), 0, 0, 0
                  ));
                  const endMs = end.getTime();
                  const nowMs = Date.now();
                  const trimmed = rangeStartRaw ? rangeStartRaw.getTime() < start.getTime() : false;
                  while (cur.getTime() <= endMs) {
                    const hk = cur.toISOString().substring(0, 13);
                    const curMs = cur.getTime();
                    rows.push({
                      label: utcKeyToLocalLabel(hk),
                      count: byHour.get(hk) || 0,
                      isFuture: curMs > nowMs,
                      wasTrimmed: false,
                    });
                    cur.setUTCHours(cur.getUTCHours() + 1);
                  }
                  // Mark the first row so the UI can hint that older data
                  // exists beyond the table.
                  if (rows.length > 0 && trimmed) rows[0].wasTrimmed = true;
                } else {
                  // `histData` is typed `any` so `histData.hourly` should
                  // infer `any[]` — but the let-typed `rows` variable is
                  // initially inferred as `never[]` (from the `rows = []`
                  // assignment in the if-branch), which makes this `map`
                  // callback's `h` parameter trigger the implicit-any
                  // rule. Annotate explicitly.
                  rows = histData.hourly.map((h: any) => ({
                    label: utcKeyToLocalLabel(h.hour),
                    count: h.count,
                    isFuture: false,
                    wasTrimmed: false,
                  }));
                }
                const maxCount = Math.max(1, ...rows.map(r => r.count));
                return (
                  <Card className="p-3 mt-3">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                        Attack timeline (per hour) — your local time
                      </h3>
                      {rows[0]?.wasTrimmed && (
                        <span className="text-[10px] text-muted-foreground" title="The table shows the last 24h; older data is still in the map and top-IPs table above.">
                          last 24h of {histData.range?.start ? new Date(histData.range.start).toLocaleString() : ''} → {histData.range?.end ? new Date(histData.range.end).toLocaleString() : ''}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {rows.map((h) => (
                        <div key={h.label} className="flex items-center gap-2 text-xs">
                          <span className={`font-mono w-[14ch] ${h.isFuture ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>
                            {h.label}
                          </span>
                          <div className="flex-1 h-3 bg-muted/30 rounded overflow-hidden">
                            <div
                              className={`h-full ${h.count === 0 ? 'bg-muted/60' : 'bg-destructive/70'}`}
                              style={{ width: `${(h.count / maxCount) * 100}%` }}
                            />
                          </div>
                          <span className={`w-[5ch] text-right font-mono ${h.count === 0 || h.isFuture ? 'text-muted-foreground/50' : ''}`}>
                            {h.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })()}

              {/* Top Countries — GeoIP summary with world map */}
              {histData.topCountries && histData.topCountries.length > 0 && (
                <Card className="p-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Top attacker countries (GeoIP)</h3>
                    <button
                      onClick={() => setMapOpen(true)}
                      className="text-[10px] text-muted-foreground hover:text-foreground underline"
                      title="Open the world map in a larger view"
                    >
                      Open map ↗
                    </button>
                  </div>
                  {/* Inline compact map */}
                  <WorldMap
                    countryCounts={histData.topCountries}
                    onCountryClick={(cc) => setHistCountryFilter(cc)}
                    highlightCode={histCountryFilter !== 'all' ? histCountryFilter : undefined}
                  />
                  {/* List below the map for accessibility + at-a-glance counts */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-border">
                    {histData.topCountries.map((c: any) => {
                      const max = histData.topCountries[0]?.count || 1;
                      const pct = (c.count / max) * 100;
                      return (
                        <button
                          key={c.countryCode}
                          onClick={() => setHistCountryFilter(c.countryCode)}
                          className={`flex items-center gap-2 text-xs p-1.5 rounded border ${histCountryFilter === c.countryCode ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-muted/30'}`}
                          title={`Filter top IPs by ${c.country}`}
                        >
                          <span className="text-base">{countryFlag(c.countryCode)}</span>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="truncate">{c.country}</span>
                              <span className="font-mono text-muted-foreground ml-2">{c.count}</span>
                            </div>
                            <div className="h-1 bg-muted/30 rounded overflow-hidden">
                              <div className="h-full bg-destructive/70" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* Full-size world map modal */}
              <Dialog open={mapOpen} onOpenChange={setMapOpen}>
                <DialogContent className="max-w-6xl w-[95vw]">
                  <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                      <span>Attack origin map ({histData?.topCountries?.length || 0} countries)</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        Click a country to filter the IP table
                      </span>
                    </DialogTitle>
                  </DialogHeader>
                  {histData?.topCountries && (
                    <WorldMap
                      countryCounts={histData.topCountries}
                      highlightCode={histCountryFilter !== 'all' ? histCountryFilter : undefined}
                      onCountryClick={(cc) => {
                        setHistCountryFilter(cc);
                        setMapOpen(false);
                      }}
                    />
                  )}
                  <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
                    <span>Bubble size = log(attack count)</span>
                    <span>•</span>
                    <span>Color = intensity (light → dark red)</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">Position = country centroid (approximate)</span>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Events table */}
              <Card className="mt-3 overflow-hidden">
                <div className="flex items-center justify-between p-3 border-b border-border flex-wrap gap-2">
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                    Events ({filteredHistEvents.length} of {histData.total} shown)
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Blocked / WL filter */}
                    <select
                      value={histStatusFilter}
                      onChange={e => setHistStatusFilter(e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-input bg-background text-foreground"
                    >
                      <option value="all">All IPs</option>
                      <option value="not-blocked">Not blocked</option>
                      <option value="blocked">Blocked (Nginx)</option>
                      <option value="whitelisted">Whitelisted (UFW)</option>
                      <option value="not-whitelisted">Not whitelisted</option>
                    </select>
                    {/* Reason filter — populated from the unique reasons in this dataset */}
                    <select
                      value={histReasonFilter}
                      onChange={e => setHistReasonFilter(e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-input bg-background text-foreground"
                    >
                      <option value="all">All reasons</option>
                      {(() => {
                        const reasons = new Set<string>();
                        histData.events.forEach((e: any) => { if (e.reason) reasons.add(e.reason); });
                        return Array.from(reasons).sort().map(r => (
                          <option key={r} value={r}>{r}</option>
                        ));
                      })()}
                    </select>
                    {/* Country filter — populated from the unique countryCodes in
                        the events (not just topIPs, so a "RU" filter on a small
                        dataset where the RU IP isn't in the top 20 still works). */}
                    <select
                      value={histCountryFilter}
                      onChange={e => setHistCountryFilter(e.target.value)}
                      className="text-xs px-2 py-1 rounded border border-input bg-background text-foreground"
                      title="Filter events by country"
                    >
                      <option value="all">All countries</option>
                      {(() => {
                        const seen = new Set<string>();
                        histData.events.forEach((e: any) => {
                          if (e.countryCode && !seen.has(e.countryCode)) seen.add(e.countryCode);
                        });
                        return Array.from(seen).sort().map(cc => (
                          <option key={cc} value={cc}>{countryFlag(cc)} {cc}</option>
                        ));
                      })()}
                    </select>
                    {(histStatusFilter !== 'all' || histReasonFilter !== 'all' || histCountryFilter !== 'all') && (
                      <button
                        onClick={() => { setHistStatusFilter('all'); setHistReasonFilter('all'); setHistCountryFilter('all'); }}
                        className="text-[10px] text-muted-foreground hover:text-foreground underline"
                      >
                        Clear filters
                      </button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // CSV export — apply filters so the CSV matches what the user sees
                        const rows = [['Timestamp', 'IP', 'Country', 'Status', 'Path', 'Reason', 'Blocked', 'Whitelisted']];
                        filteredHistEvents.forEach((e: any) => {
                          rows.push([
                            e.ts, e.ip, e.countryCode || '', e.status, e.path, e.reason,
                            denyIpSet.has(e.ip) ? 'yes' : 'no',
                            whitelistedIpSet.has(e.ip) ? 'yes' : 'no',
                          ]);
                        });
                        const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `opsec-historical-${histStart.replace(/[:T]/g, '-')}_${histEnd.replace(/[:T]/g, '-')}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      <FileText className="w-3 h-3 mr-1" /> CSV
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-card text-muted-foreground sticky top-0 z-10 shadow-sm">
                      <tr>
                        <th className="text-left p-2 font-medium">Timestamp</th>
                        <th className="text-left p-2 font-medium">IP</th>
                        <th className="text-left p-2 font-medium" title="Country of origin (from GeoIP)">Country</th>
                        <th className="text-left p-2 font-medium">Status</th>
                        <th className="text-left p-2 font-medium">Path</th>
                        <th className="text-left p-2 font-medium">Reason</th>
                        <th className="text-right p-2 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistEvents.map((e: any, i: number) => (
                        <tr key={`${e.ip}-${e.ts}-${i}`} className="border-t border-border">
                          <td className="p-2 text-muted-foreground font-mono whitespace-nowrap">
                            {new Date(e.ts).toLocaleString()}
                          </td>
                          <td className="p-2 font-mono">{e.ip}</td>
                          <td className="p-2 whitespace-nowrap" title={e.country || ''}>
                            {e.countryCode ? (
                              <span className="inline-flex items-center gap-1">
                                <span>{countryFlag(e.countryCode)}</span>
                                <span className="text-[10px] text-muted-foreground">{e.countryCode}</span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">—</span>
                            )}
                          </td>
                          <td className="p-2">{e.status}</td>
                          <td className="p-2 font-mono max-w-[30ch] truncate" title={e.path}>{e.path}</td>
                          <td className="p-2 text-muted-foreground">{e.reason}</td>
                          <td className="p-2 text-right">
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              {denyIpSet.has(e.ip) ? (
                                <button
                                  disabled={busy[`hunnginx-${e.ip}`]}
                                  onClick={() => doAction(`hunnginx-${e.ip}`, () => api.opsecRemoveDenyIp(e.ip))}
                                  className="px-1.5 py-0.5 border border-amber-500/50 text-amber-600 dark:text-amber-400 rounded text-[10px] hover:bg-amber-500/10 disabled:opacity-50"
                                  title="Remove from Nginx deny list (currently blocked)"
                                >
                                  Unblock
                                </button>
                              ) : (
                                <button
                                  disabled={busy[`hblock-${e.ip}`]}
                                  onClick={() => doAction(`hblock-${e.ip}`, () => api.opsecDenyIp(e.ip, `Blocked from historical view: ${e.reason || e.path}`))}
                                  className="px-1.5 py-0.5 border border-destructive/50 text-destructive rounded text-[10px] hover:bg-destructive/10 disabled:opacity-50"
                                  title="Block in Nginx"
                                >
                                  Block
                                </button>
                              )}
                              {whitelistedIpSet.has(e.ip) ? (
                                <button
                                  disabled={busy[`hrmwl-${e.ip}`]}
                                  onClick={() => doAction(`hrmwl-${e.ip}`, () => api.opsecRemoveWhitelist(e.ip))}
                                  className="px-1.5 py-0.5 border border-green-500/50 text-green-600 dark:text-green-400 rounded text-[10px] hover:bg-green-500/10 disabled:opacity-50"
                                  title="Remove from whitelist (currently whitelisted)"
                                >
                                  UnWL
                                </button>
                              ) : (
                                <button
                                  disabled={busy[`hwl-${e.ip}`]}
                                  onClick={() => doAction(`hwl-${e.ip}`, () => api.opsecWhitelistIp(e.ip, 'Whitelisted from historical view'))}
                                  className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                                  title="Whitelist (known good)"
                                >
                                  WL
                                </button>
                              )}
                              <button
                                onClick={() => setReportRow({
                                  ip: e.ip,
                                  path: e.path,
                                  reason: e.reason,
                                  time: e.ts,
                                  status: e.status,
                                })}
                                className="px-1.5 py-0.5 border border-input rounded text-[10px] hover:bg-background"
                                title="Report (AbuseIPDB)"
                              >
                                RPT
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {histData.events.length === 0 && (
                        <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No events in this range</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}

          {/* Drill-down modal: all events for one IP in the date range.
              Force a wide desktop layout — the DialogContent component has
              its own default max-width that overrides Tailwind classes
              unless we use !important. 1400px is wider than the page
              (max-w-6xl) so the modal is clearly the focal point. On
              mobile it falls back to 95vw. */}
          <Dialog open={!!histDrillIp} onOpenChange={(o) => {
            if (!o) {
              setHistDrillIp(null);
              setHistDrillData(null);
            }
          }}>
            <DialogContent
              className="!max-w-[1400px] w-[95vw] max-h-[90vh]"
              style={{ maxWidth: '1400px' }}
            >
              <DialogHeader>
                <DialogTitle>
                  All events for {histDrillIp}
                  {(() => {
                    const ipRow = histData?.topIPs?.find((r: any) => r.ip === histDrillIp);
                    if (ipRow?.countryCode) {
                      return (
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                          {countryFlag(ipRow.countryCode)} {ipRow.country}
                          {ipRow.city ? ` · ${ipRow.city}` : ''}
                          {ipRow.isp ? ` · ${ipRow.isp}` : ''}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {histData?.range ? ` (${new Date(histData.range.start).toLocaleString()} → ${new Date(histData.range.end).toLocaleString()})` : ''}
                  {histDrillData && (
                    <span className="text-xs text-muted-foreground font-normal ml-2">
                      ({histDrillData.length} event{histDrillData.length === 1 ? '' : 's'})
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              {histDrillIp && (
                <div className="max-h-[70vh] overflow-y-auto">
                  {histDrillLoading ? (
                    <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading events for {histDrillIp}…
                    </div>
                  ) : !histDrillData ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">No data</div>
                  ) : histDrillData.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">No events found for this IP in the date range.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="bg-card text-muted-foreground sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium whitespace-nowrap">Timestamp</th>
                          <th className="text-left p-2 font-medium">Status</th>
                          <th className="text-left p-2 font-medium">Method</th>
                          <th className="text-left p-2 font-medium">Path</th>
                          <th className="text-right p-2 font-medium whitespace-nowrap">Bytes</th>
                          <th className="text-left p-2 font-medium">Reason</th>
                          <th className="text-left p-2 font-medium">User agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {histDrillData.map((e: any, i: number) => (
                          <tr key={`drill-${e.ts}-${i}`} className="border-t border-border">
                            <td className="p-2 text-muted-foreground font-mono whitespace-nowrap">
                              {new Date(e.ts).toLocaleString()}
                            </td>
                            <td className="p-2">{e.status}</td>
                            <td className="p-2 font-mono text-muted-foreground">{e.method || 'GET'}</td>
                            <td className="p-2 font-mono max-w-[40ch] truncate" title={e.path}>{e.path}</td>
                            <td className="p-2 text-right text-muted-foreground font-mono">{e.bytes || '—'}</td>
                            <td className="p-2 text-muted-foreground">{e.reason}</td>
                            <td className="p-2 text-muted-foreground max-w-[30ch] truncate" title={e.userAgent || ''}>
                              {e.userAgent || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </section>
        )}

        {/* === Section 4: UFW Firewall === */}
        {activeTab === 'lists' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <Ban className="w-4 h-4 text-destructive" />
            {t('network.opsecUfwTitle') || 'UFW Firewall'}
          </h2>
          <Card className="p-3">
            <form onSubmit={submitBlock} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end">
              <div>
                <Label>{t('network.opsecIp') || 'IP address'}</Label>
                <Input value={blockIp} onChange={e => setBlockIp(e.target.value)} placeholder="1.2.3.4" />
              </div>
              <div>
                <Label>{t('network.opsecReason') || 'Reason'}</Label>
                <Input value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="Manual block" />
              </div>
              <Button type="submit" disabled={busy['submitBlock']}>
                {t('network.opsecBlock') || 'Block'}
              </Button>
            </form>
          </Card>

          <div className="mt-3">
            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('network.opsecBlockedList') || 'Blocked IPs'} ({blockedIps.length})
                </h3>
                <Input
                  placeholder={t('common.search') || 'Search…'}
                  value={blockedSearch}
                  onChange={e => setBlockedSearch(e.target.value)}
                  className="!py-1 !text-xs w-32"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {blockedIps
                      .filter((b: any) => !blockedSearch || String(b.ip).includes(blockedSearch))
                      .map((b: any) => (
                        <tr key={b.number} className="border-t border-border">
                          <td className="p-1.5 font-mono">{b.ip}</td>
                          <td className="p-1.5 text-muted-foreground truncate">{b.rule}</td>
                          <td className="p-1.5 text-right">
                            <button
                              disabled={busy[`unblock-${b.ip}`]}
                              onClick={() => doAction(`unblock-${b.ip}`, () => api.opsecUnblockIp(b.ip))}
                              className="px-2 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    {blockedIps.length === 0 && (
                      <tr><td colSpan={3} className="p-3 text-center text-muted-foreground">—</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Block log moved to Logs tab */}
          </div>
        </section>
        )}

        {/* === Section 5: Nginx Deny List === */}
        {activeTab === 'lists' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            {t('network.opsecNginxTitle') || 'Nginx deny list'}
          </h2>
          <Card className="p-3">
            <form onSubmit={submitDeny} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <Label>{t('network.opsecIpOrCidr') || 'IP or CIDR'}</Label>
                <Input value={denyCidr} onChange={e => setDenyCidr(e.target.value)} placeholder="1.2.3.4 or 1.2.3.0/24" />
              </div>
              <Button type="submit" disabled={busy['submitDeny']}>
                {t('network.opsecAdd') || 'Add'}
              </Button>
            </form>
          </Card>

          <div className="mt-3">
            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('network.opsecDenyList') || 'Denied (Nginx)'} ({denyIps.length})
                </h3>
                <Input
                  placeholder={t('common.search') || 'Search…'}
                  value={denySearch}
                  onChange={e => setDenySearch(e.target.value)}
                  className="!py-1 !text-xs w-32"
                />
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {denyIps
                      .filter(ip => !denySearch || ip.includes(denySearch))
                      .map(ip => (
                        <tr key={ip} className="border-t border-border">
                          <td className="p-1.5 font-mono">{ip}</td>
                          <td className="p-1.5 text-right">
                            <button
                              disabled={busy[`rm-${ip}`]}
                              onClick={() => doAction(`rm-${ip}`, () => api.opsecRemoveDenyIp(ip))}
                              className="px-2 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    {denyIps.length === 0 && (
                      <tr><td colSpan={2} className="p-3 text-center text-muted-foreground">—</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Deny log moved to Logs tab */}
          </div>
        </section>
        )}

        {/* === Section 6: Whitelisted IPs === */}
        {activeTab === 'lists' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            {t('network.opsecWhitelistTitle') || 'Whitelisted IPs'}
          </h2>
          <Card className="p-3">
            <form onSubmit={submitWhitelist} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end">
              <div>
                <Label>{t('network.opsecIp') || 'IP address'}</Label>
                <Input value={whitelistIp} onChange={e => setWhitelistIp(e.target.value)} placeholder="1.2.3.4" />
              </div>
              <div>
                <Label>{t('network.opsecReason') || 'Reason'}</Label>
                <Input value={whitelistReason} onChange={e => setWhitelistReason(e.target.value)} placeholder="Office ISP" />
              </div>
              <Button type="submit" disabled={busy['submitWhitelist']}>
                {t('network.opsecWhitelist') || 'Whitelist'}
              </Button>
            </form>
          </Card>

          <Card className="p-3 mt-3">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              {t('network.opsecWhitelistList') || 'Whitelisted'} ({whitelistedIps.length})
            </h3>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-card text-muted-foreground sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="text-left p-2 font-medium">IP</th>
                    <th className="text-left p-2 font-medium">{t('network.opsecReason') || 'Reason'}</th>
                    <th className="text-left p-2 font-medium">{t('network.opsecAddedAt') || 'Added'}</th>
                    <th className="text-right p-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {whitelistedIps.map((w: any) => (
                    <tr key={w.ip} className="border-t border-border">
                      <td className="p-2 font-mono">{w.ip}</td>
                      <td className="p-2 text-muted-foreground">{w.reason || '—'}</td>
                      <td className="p-2 text-muted-foreground">{w.added_at ? fmtTime(w.added_at) : '—'}</td>
                      <td className="p-2 text-right">
                        <button
                          disabled={busy[`rmwl-${w.ip}`]}
                          onClick={() => doAction(`rmwl-${w.ip}`, () => api.opsecRemoveWhitelist(w.ip))}
                          className="px-2 py-0.5 border border-input rounded text-[10px] hover:bg-background disabled:opacity-50"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {whitelistedIps.length === 0 && (
                    <tr><td colSpan={4} className="p-3 text-center text-muted-foreground">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
        )}

        {/* === Logs tab: Block log + Deny log === */}
        {/* Both logs include their own date filter + CSV export. The deny
            log is read from the live file (after logrotate creates a new
            one, history is in the dated backup — the backend reads both). */}
        {activeTab === 'logs' && (
        <section>
          <h2 className="text-base font-bold sm:text-lg text-foreground mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            Block log & Deny log
          </h2>
          <div className="space-y-4">
            <LogCard
              title={t('network.opsecBlockLog') || 'Block log'}
              log={blockLog}
              expandedKey="block"
              logExpanded={logExpanded}
              setLogExpanded={setLogExpanded}
            />
            <LogCard
              title={t('network.opsecDenyLog') || 'Deny log'}
              log={denyLog}
              expandedKey="deny"
              logExpanded={logExpanded}
              setLogExpanded={setLogExpanded}
              filter="all"
              showDateFilter
              csvFilename="deny-log"
            />
          </div>
        </section>
        )}

        {/* === Report modal === */}
        <Dialog open={!!reportRow} onOpenChange={(o) => !o && setReportRow(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-2">
                <span>AbuseIPDB Report — {reportRow?.ip}</span>
                {reportRow && (() => {
                  const r = buildReportComment(reportRow.ip);
                  return (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">Category:</span>
                      <select
                        value={r.category}
                        onChange={(e) => {
                          const newCat = e.target.value;
                          // Regenerate the comment with the new category
                          const fresh = buildReportComment(reportRow.ip);
                          const newComment = fresh.comment.replace(
                            /Category: .* \(AbuseIPDB IDs: .*\)/,
                            `Category: ${newCat} (AbuseIPDB IDs: ${(CATEGORY_NUM as any)[newCat] || '22'})`
                          );
                          setReportCommentOverride(newComment);
                          setReportCategory(newCat);
                        }}
                        className="text-xs px-2 py-1 rounded border border-input bg-background text-foreground font-normal"
                      >
                        {Object.keys(CATEGORY_NUM).map((cat) => (
                          <option key={cat} value={cat}>{cat} ({(CATEGORY_NUM as any)[cat]})</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}
              </DialogTitle>
            </DialogHeader>
            {reportRow && (
              <ReportModalBody
                ip={reportRow.ip}
                initialComment={reportCommentOverride || buildReportComment(reportRow.ip).comment}
                onClose={() => {
                  setReportRow(null);
                  setReportCommentOverride(null);
                  setReportCategory(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StatCard({ label, value, sub, danger }: { label: string; value: any; sub?: string; danger?: boolean }) {
  return (
    <Card className={`p-3 ${danger ? 'border-destructive/50 bg-destructive/5' : ''}`}>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${danger ? 'text-destructive' : 'text-foreground'}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: any; tone?: 'ok' | 'warn' | 'err' }) {
  const toneCls =
    tone === 'ok' ? 'text-green-600' :
    tone === 'warn' ? 'text-amber-600' :
    tone === 'err' ? 'text-red-600' : '';
  return (
    <div className="bg-card border border-border rounded p-2 text-center">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-semibold ${toneCls}`}>{value}</p>
    </div>
  );
}

function LogCard({ title, log, expandedKey, logExpanded, setLogExpanded, filter, showDateFilter, csvFilename }: {
  title: string; log: string; expandedKey: string;
  logExpanded: { [k: string]: boolean };
  setLogExpanded: React.Dispatch<React.SetStateAction<{ [k: string]: boolean }>>;
  filter?: 'all' | 'AUTO' | 'MANUAL';
  showDateFilter?: boolean;
  csvFilename?: string;
}) {
  const [activeFilter, setActiveFilter] = useState<'all' | 'AUTO' | 'MANUAL'>(filter || 'all');
  const [dateRange, setDateRange] = useState<'24h' | '7d' | '30d' | 'all'>('24h');

  // Parse log lines, extract ISO timestamp from each, filter by date range + source.
  const allLines = log ? log.split('\n').filter(Boolean) : [];
  const sourceFiltered = activeFilter === 'all'
    ? allLines
    : allLines.filter((l) => l.includes(`${activeFilter}-BLOCKED`));
  const cutoff = (() => {
    if (dateRange === 'all') return 0;
    const ms = dateRange === '24h' ? 24*3600*1000
             : dateRange === '7d'  ? 7*24*3600*1000
             : 30*24*3600*1000;
    return Date.now() - ms;
  })();
  const lines = sourceFiltered.filter((l) => {
    if (cutoff === 0) return true;
    // ISO timestamp is the first token in each line
    const ts = l.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/);
    if (!ts) return true; // keep lines we can't parse
    return new Date(ts[1]).getTime() >= cutoff;
  }).reverse(); // newest on top

  const expanded = !!logExpanded[expandedKey];
  const shown = expanded ? lines : lines.slice(0, 12);

  function downloadCsv() {
    if (lines.length === 0) return;
    // Log format: <ISO> | <user> | <action: ip> | <reason>
    // Convert pipe-separated to CSV with proper escaping.
    // Convert ISO timestamp to local time (same as display).
    const header = 'timestamp_local,user,action_ip,reason';
    const rows = lines.map((l) => {
      const m = l.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)\s*\|/);
      let ts = m ? m[1] : '';
      if (ts) {
        const d = new Date(ts);
        if (!isNaN(d.getTime())) {
          const pad = (n: number) => String(n).padStart(2, '0');
          ts = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
      }
      const rest = m ? l.substring(m[0].length) : l;
      const parts = rest.split(' | ').map((s) => s.trim());
      // ts + parts: [local, user, "NGINX DENY ADDED: 1.2.3.4", "AUTO-BLOCKED: 97% abuse confidence"]
      return [ts, ...parts].map((p) => {
        if (p.includes(',') || p.includes('"') || p.includes('\n')) {
          return '"' + p.replace(/"/g, '""') + '"';
        }
        return p;
      }).join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${csvFilename || 'log'}-${dateRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
  }

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
          <FileText className="w-3 h-3" /> {title} ({activeFilter === 'all' ? lines.length : `${lines.length}/${allLines.length}`})
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {showDateFilter && (
            <div className="flex items-center gap-1 text-[10px]">
              {(['24h', '7d', '30d', 'all'] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setDateRange(r)}
                  className={`px-1.5 py-0.5 rounded border ${
                    dateRange === r
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:bg-background'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
          {filter && (
            <div className="flex items-center gap-1 text-[10px]">
              {(['all', 'AUTO', 'MANUAL'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-1.5 py-0.5 rounded border ${
                    activeFilter === f
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input text-muted-foreground hover:bg-background'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
          {lines.length > 0 && (
            <button
              onClick={downloadCsv}
              title="Export filtered log as CSV"
              className="text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 border border-input rounded"
            >
              ⬇ CSV
            </button>
          )}
          {lines.length > 12 && (
            <button
              onClick={() => setLogExpanded(p => ({ ...p, [expandedKey]: !p[expandedKey] }))}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}
        </div>
      </div>
      <pre className="text-[10px] font-mono text-muted-foreground whitespace-pre-wrap break-all max-h-64 overflow-y-auto bg-muted/30 p-2 rounded">
        {shown.length ? shown.map(fmtLogLine).join('\n') : '—'}
      </pre>
    </Card>
  );
}

function ReportModalBody({ ip, initialComment, onClose }: { ip: string; initialComment: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState(initialComment);
  const [copied, setCopied] = useState(false);
  // The EJS's "openAbuseReport" only passes the IP, not the comment — the
  // user pastes the comment into AbuseIPDB's form after selecting category.
  // We match that behavior: Open AbuseIPDB just goes to the report page.
  //
  // Use a real <a target="_blank"> rather than window.open(). PWA-installed
  // Chrome and mobile browsers aggressively block window.open() even from
  // user-initiated button clicks, but anchor tags with target="_blank" are
  // always honored.
  const reportUrl = `https://www.abuseipdb.com/report?ip=${encodeURIComponent(ip)}`;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t('network.opsecReportHint') || 'After copying, go to AbuseIPDB, select the category, paste the comment, and submit.'}
      </p>
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        rows={10}
        className="w-full bg-muted/30 border border-input rounded p-2 text-xs font-mono text-foreground"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            navigator.clipboard?.writeText(comment);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy to Clipboard</>}
        </Button>
        <a
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-3 h-8 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <ExternalLink className="w-3 h-3 mr-1" /> Open AbuseIPDB
        </a>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t('common.close') || 'Close'}
        </Button>
      </div>
    </div>
  );
}
